var __merlinGlobal = (typeof window !== 'undefined' ? window : globalThis);
var __merlinTrustedTypes = __merlinGlobal.MerlinTrustedTypes || (__merlinGlobal.MerlinTrustedTypes = (function() {
    function safeSetInnerHTML(element, html) {
        if (!element) return;
        if (!html) {
            element.replaceChildren();
            return;
        }

        var doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
        element.replaceChildren();
        Array.from(doc.body.childNodes).forEach(function(node) {
            element.appendChild(document.adoptNode(node));
        });
    }

    function safeInsertAdjacentHTML(element, position, html) {
        if (!element || !html) return;

        var doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
        var fragment = document.createDocumentFragment();
        Array.from(doc.body.childNodes).forEach(function(node) {
            fragment.appendChild(document.adoptNode(node));
        });

        switch (position) {
            case 'beforeend':
                element.appendChild(fragment);
                break;
            case 'afterbegin':
                element.prepend(fragment);
                break;
            case 'beforebegin':
                element.before(fragment);
                break;
            case 'afterend':
                element.after(fragment);
                break;
        }
    }

    return {
        safeSetInnerHTML: safeSetInnerHTML,
        safeInsertAdjacentHTML: safeInsertAdjacentHTML
    };
})());
var safeSetInnerHTML = __merlinTrustedTypes.safeSetInnerHTML;
var safeInsertAdjacentHTML = __merlinTrustedTypes.safeInsertAdjacentHTML;
/**
 * Graph Explorer - API Module
 * Handles dynamic graph updates via API calls without rebuilding HTML
 */

(function(global) {
    'use strict';

    // ============================================
    // API State
    // ============================================
    const apiState = {
        apiEndpoint: null,
        workspaceId: null,
        cursorId: null,
        instanceId: null,
        isLoading: false,
        lastUpdate: null,
        lastDataHash: null,    // Hash of last graph data to detect changes
        pollingInterval: null,
        pollingEnabled: false,
        pollingDelayMs: 5000,  // 5 seconds default
        authToken: null,       // Bearer token from session storage or options
        apiKey: null,          // X-API-Key fallback
        authMethod: null,      // 'bearer', 'apikey', or 'cookie'
        aliases: [],           // Cached list of aliases
        isMCPContext: false,   // True when embedded in MCP client iframe
        // Legend filters — passed as query params to /graph/data
        activeNodeTypeFilter: null,   // Comma-separated node types (e.g. 'taxonomy_resources,ontologies')
        activeEdgeNameFilter: null,   // Comma-separated edge names (e.g. 'subClassOf,belongs_to_taxonomy')
        entrypointTaxonomyId: null,
        trimFilter: false,
        // Aggregation / extended edge controls
        extendedEdges: true,          // Include auto-discovered cross-alias edges (ontology/relationship edges)
        maxNodes: 5000,                  // Server-side clustering budget (0=disabled)
        // Server-side layout
        layoutAlgorithm: null,        // 'spring', 'kamada_kawai', 'spectral', etc. null=auto
        autoLayout: true,             // Let server auto-pick layout algorithm
        layoutConfig: null,           // JSON-able dict: {scale, k, iterations, seed}
        _edgeRevealTimer: null,       // Timer for deferred edge reveal
        _edgeRevealCleanup: null,     // Cleanup callback for deferred edge reveal listeners
        _deferredEdgeIds: null,       // Edge IDs currently hidden during stabilization
        _edgeRevealGeneration: 0,
        _nodeLabelRevealTimer: null,
        _nodeLabelRevealCleanup: null,
        _deferredNodeLabelIds: null,
        _nodeLabelRevealGeneration: 0,
        performanceMode: 'auto',      // 'auto' or 'large'
        largeGraphProfileActive: false,
        interactionDeferred: false,
        _interactionResumeTimer: null,
        _interactionResumeCleanup: null,
        _interactionResumeGeneration: 0,
        standaloneSnapshot: null,
        pointerGraphCache: {}
    };

    const CURSOR_SESSION_KEY = 'explorerCursorId';
    const PERFORMANCE_MODE_STORAGE_KEY = 'explorerPerformanceMode';

    // AbortController for in-flight graph data fetches.
    // Replaced on every refreshGraph() call so stale requests are cancelled
    // when the user rapidly switches checkpoints/bookmarks.
    let _graphFetchAbortController = null;

    // AbortController for in-flight side-data fetches (state + documents).
    // Replaced on every _refreshSideData() call so stale requests are cancelled.
    let _sideDataAbortController = null;

    // Monotonically increasing generation counter so _refreshSideData() can
    // discard results from an older generation.
    let _sideDataGeneration = 0;

    // ============================================
    // Initialization
    // ============================================
    function cloneStandalonePayload(payload, fallbackValue = null) {
        if (payload === null || payload === undefined) {
            return fallbackValue;
        }
        try {
            return JSON.parse(JSON.stringify(payload));
        } catch (error) {
            console.warn('[ExplorerAPI] Failed to clone standalone payload:', error);
            return fallbackValue;
        }
    }

    function loadPersistedPerformanceMode() {
        try {
            var stored = localStorage.getItem(PERFORMANCE_MODE_STORAGE_KEY);
            return stored === 'large' ? 'large' : 'auto';
        } catch (error) {
            return 'auto';
        }
    }

    function persistPerformanceMode(mode) {
        try {
            localStorage.setItem(PERFORMANCE_MODE_STORAGE_KEY, mode);
        } catch (error) {
            // Ignore storage failures.
        }
    }

    function hasStandaloneSnapshot() {
        const snapshot = apiState.standaloneSnapshot;
        return !!(
            snapshot && typeof snapshot === 'object' && (
                snapshot.graphData ||
                snapshot.cursorState ||
                snapshot.documentsData ||
                snapshot.cursorHistory ||
                snapshot.pointerGraphs ||
                snapshot.aliasContents
            )
        );
    }

    function getStandaloneGraphData() {
        return cloneStandalonePayload(
            apiState.standaloneSnapshot && apiState.standaloneSnapshot.graphData,
            { error: 'Standalone graph snapshot missing', nodes: [], edges: [] }
        );
    }

    function getStandalonePointerGraph(pointerType, pointerName) {
        const pointerGraphs = apiState.standaloneSnapshot && apiState.standaloneSnapshot.pointerGraphs;
        if (!pointerGraphs) {
            return null;
        }
        return cloneStandalonePayload(pointerGraphs[`${pointerType}:${pointerName}`], null);
    }

    function getStandaloneCursorState() {
        return cloneStandalonePayload(
            apiState.standaloneSnapshot && apiState.standaloneSnapshot.cursorState,
            null
        );
    }

    function getStandaloneDocumentsData() {
        return cloneStandalonePayload(
            apiState.standaloneSnapshot && apiState.standaloneSnapshot.documentsData,
            null
        );
    }

    function getStandaloneCursorHistory() {
        return cloneStandalonePayload(
            apiState.standaloneSnapshot && apiState.standaloneSnapshot.cursorHistory,
            null
        );
    }

    function getStandaloneAliasContent(aliasName) {
        const aliasContents = apiState.standaloneSnapshot && apiState.standaloneSnapshot.aliasContents;
        if (!aliasContents) {
            return null;
        }
        return cloneStandalonePayload(aliasContents[aliasName], null);
    }

    function init(options = {}) {
        // Load config from options or explorerConfig (passed from HTML template)
        // Note: Empty string "" is valid for dev mode with Vite proxy (relative URLs)
        apiState.apiEndpoint = options.apiEndpoint !== undefined ? options.apiEndpoint : 
                               (options.config?.apiEndpoint !== undefined ? options.config.apiEndpoint : null);
        apiState.workspaceId = options.workspaceId || options.config?.workspaceId || null;
        // Default cursor_id to 'me' - API resolves to user's default cursor
        const storedCursorId = loadCursorIdFromSession();
        apiState.cursorId = options.cursorId || options.config?.cursorId || storedCursorId || 'me';
        apiState.instanceId = options.instanceId || options.config?.instanceId || null;
        apiState.standaloneSnapshot = (
            options.standaloneSnapshot && typeof options.standaloneSnapshot === 'object'
        )
            ? options.standaloneSnapshot
            : ((options.config?.standaloneSnapshot && typeof options.config.standaloneSnapshot === 'object')
                ? options.config.standaloneSnapshot
                : null);
        apiState.performanceMode = (options.performanceMode || options.config?.performanceMode || loadPersistedPerformanceMode()) === 'large'
            ? 'large'
            : 'auto';
        persistPerformanceMode(apiState.performanceMode);

        const configuredMaxNodes = options.maxNodes !== undefined
            ? options.maxNodes
            : options.config?.maxNodes;
        if (configuredMaxNodes !== undefined && configuredMaxNodes !== null && configuredMaxNodes !== '') {
            apiState.maxNodes = Math.max(0, parseInt(configuredMaxNodes, 10) || 0);
        } else if (apiState.standaloneSnapshot) {
            // Standalone exports embed a full snapshot; keep later refreshes unclustered
            // so deselect/reset actions do not collapse the saved graph back to a 5000-node budget.
            apiState.maxNodes = 0;
        }

        // First check if auth was passed via options
        if (options.authToken) {
            apiState.authToken = options.authToken;
            apiState.authMethod = 'bearer';
            console.log('[ExplorerAPI] Bearer token loaded from options');
        } else if (options.apiKey) {
            apiState.apiKey = options.apiKey;
            apiState.authMethod = 'apikey';
            console.log('[ExplorerAPI] API key loaded from options');
        } else if (options.useCookieAuth || options.config?.useCookieAuth) {
            // MCP UI session mode - auth via HttpOnly cookie
            apiState.authMethod = 'cookie';
            console.log('[ExplorerAPI] Cookie-based auth enabled (MCP UI session)');
        } else {
            // Fall back to session storage
            loadAuthFromSessionStorage();
        }

        // Check for MCP context (embedded in iframe with MCP intent support)
        apiState.isMCPContext = options.isMCPContext || options.config?.isMCPContext || false;
        if (apiState.isMCPContext) {
            console.log('[ExplorerAPI] Running in MCP context - intent communication enabled');
            // In MCP context with cookie auth, we're ready to go
            if (apiState.authMethod === 'cookie') {
                console.log('[ExplorerAPI] MCP context with cookie auth - ready for API calls');
            }
        }

        console.log('[ExplorerAPI] Initialized', {
            apiEndpoint: apiState.apiEndpoint,
            workspaceId: apiState.workspaceId,
            cursorId: apiState.cursorId,
            authMethod: apiState.authMethod,
            isMCPContext: apiState.isMCPContext,
            hasStandaloneSnapshot: hasStandaloneSnapshot(),
            maxNodes: apiState.maxNodes,
            performanceMode: apiState.performanceMode
        });
        
        // Update API key button visibility (now hidden)
        updateApiKeyButtonVisibility();
        
        // Removed: Auto-prompt for API key - we now rely on bearer token auth only
    }
    
    function isConfigured() {
        // Empty string "" is valid for apiEndpoint (dev mode with relative URLs)
        // Check explicitly for null/undefined rather than truthy
        const hasEndpoint = apiState.apiEndpoint !== null && apiState.apiEndpoint !== undefined;
        const hasWorkspace = !!apiState.workspaceId;
        const hasCursor = !!apiState.cursorId;
        return hasEndpoint && hasWorkspace && hasCursor;
    }
    
    function isReady() {
        // API is ready when configured AND has authentication
        return isConfigured() && hasAuthentication();
    }
    
    // ============================================
    // Authentication Handling
    // ============================================
    function loadAuthFromSessionStorage() {
        try {
            const userStr = sessionStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                if (user && user.token) {
                    apiState.authToken = user.token;
                    apiState.authMethod = 'bearer';
                    console.log('[ExplorerAPI] Bearer token loaded from session storage');
                    return true;
                }
            }
        } catch (e) {
            console.warn('[ExplorerAPI] Failed to parse user from session storage:', e);
        }
        
        // Check for stored API key as fallback
        const storedApiKey = sessionStorage.getItem('explorerApiKey');
        if (storedApiKey) {
            apiState.apiKey = storedApiKey;
            apiState.authMethod = 'apikey';
            console.log('[ExplorerAPI] API key loaded from session storage');
            return true;
        }
        
        apiState.authMethod = null;
        console.log('[ExplorerAPI] No authentication found');
        return false;
    }

    function loadCursorIdFromSession() {
        try {
            return sessionStorage.getItem(CURSOR_SESSION_KEY);
        } catch (e) {
            return null;
        }
    }

    function persistCursorId(cursorId) {
        try {
            if (cursorId) {
                sessionStorage.setItem(CURSOR_SESSION_KEY, cursorId);
            } else {
                sessionStorage.removeItem(CURSOR_SESSION_KEY);
            }
        } catch (e) {
            // Ignore storage errors
        }
    }

    function setCursorId(cursorId, persist = true) {
        if (!cursorId) return;
        apiState.cursorId = cursorId;
        if (persist) {
            persistCursorId(cursorId);
        }
    }

    function getPointerCacheKey(pointerType, pointerName) {
        return `${apiState.cursorId || ''}:${pointerType}:${pointerName}`;
    }

    function getCachedPointerGraph(pointerType, pointerName) {
        const key = getPointerCacheKey(pointerType, pointerName);
        if (apiState.pointerGraphCache && apiState.pointerGraphCache[key]) {
            return cloneStandalonePayload(apiState.pointerGraphCache[key]);
        }
        return null;
    }

    function cachePointerGraph(pointerType, pointerName, data) {
        if (!data || data.error) return;
        if (!apiState.pointerGraphCache) {
            apiState.pointerGraphCache = {};
        }
        apiState.pointerGraphCache[getPointerCacheKey(pointerType, pointerName)] = data;
    }

    function clearPointerGraphCache(cursorId = null) {
        if (!apiState.pointerGraphCache) {
            apiState.pointerGraphCache = {};
            return;
        }
        if (!cursorId) {
            apiState.pointerGraphCache = {};
            return;
        }
        const prefix = `${cursorId}:`;
        for (const key of Object.keys(apiState.pointerGraphCache)) {
            if (key.startsWith(prefix)) {
                delete apiState.pointerGraphCache[key];
            }
        }
    }

    async function previewPointerGraph(pointerType, pointerName, options = {}) {
        if (!pointerName) return null;

        const data = await fetchGraphDataForPointer(pointerType, pointerName);
        if (!data || data.error) {
            return data;
        }

        updateGraph(data, options.forceUpdate !== false);

        if (pointerType === 'bookmark' && typeof ExplorerTabs !== 'undefined') {
            if (options.switchTab !== false) {
                ExplorerTabs.switchTab('bookmarks');
            }
            ExplorerTabs.setBookmarks(options.bookmarkList, pointerName);
        } else if (pointerType === 'checkpoint' && typeof ExplorerTabs !== 'undefined') {
            if (options.switchTab !== false) {
                ExplorerTabs.switchTab('graph');
            }
            ExplorerTabs.setCheckpoints(options.checkpointList, pointerName);
        }

        return data;
    }

    async function navigateToBookmark(bookmarkName, options = {}) {
        if (!bookmarkName) return null;

        if (options.previewOnly) {
            return previewPointerGraph('bookmark', bookmarkName, options);
        }

        const escaped = String(bookmarkName).replace(/'/g, "''");
        if (options.switchTab !== false && typeof ExplorerTabs !== 'undefined') {
            ExplorerTabs.switchTab('bookmarks');
            ExplorerTabs.setBookmarks(undefined, bookmarkName);
        }

        await executeMQL(`USE BOOKMARK('${escaped}');`);
        await refreshGraph(true);
        return bookmarkName;
    }

    async function applyPreWarmedTaskCursor(taskData, options = {}) {
        if (!taskData) return null;

        const bootstrap = (taskData.context && taskData.context.cursor_bootstrap) || {};
        const bookmarkMeta = bootstrap.bookmarks || {};
        const taskCursorId = taskData.cursor_id || bootstrap.cursor_id;

        if (taskCursorId) {
            setCursorId(taskCursorId, true);
            if (typeof ExplorerCursor !== 'undefined' && ExplorerCursor.syncCursorSelection) {
                ExplorerCursor.syncCursorSelection(taskCursorId);
            }
        }

        await _refreshSideData();

        let bookmarkNames = Object.keys(bookmarkMeta);
        if (bookmarkNames.length === 0) {
            try {
                const stateData = await fetchCursorState();
                const raw = stateData?.bookmarks || [];
                if (Array.isArray(raw)) {
                    bookmarkNames = raw.map((bm) => bm.name || bm).filter(Boolean);
                } else if (raw && typeof raw === 'object') {
                    bookmarkNames = Object.keys(raw);
                }
            } catch (err) {
                console.warn('[ExplorerAPI] Failed to read bookmarks for task cursor:', err);
            }
        }

        const preferred = options.bookmark
            || PREWARMED_BOOKMARK_PRIORITY.find((name) => bookmarkNames.includes(name))
            || bookmarkNames[0];

        if (!preferred) {
            await refreshGraph(true);
            return { cursor_id: taskCursorId, bookmark: null };
        }

        const bookmarkList = bookmarkNames.map((name) => ({ name }));
        if (typeof ExplorerTabs !== 'undefined') {
            if (options.switchTab !== false) {
                ExplorerTabs.switchTab('bookmarks');
            }
            ExplorerTabs.setBookmarks(bookmarkList, preferred);
        }
        if (typeof ExplorerToolbar !== 'undefined' && ExplorerToolbar.updateBookmarksList) {
            ExplorerToolbar.updateBookmarksList(bookmarkList);
        }

        await previewPointerGraph('bookmark', preferred, {
            switchTab: false,
            bookmarkList,
        });

        const remaining = bookmarkNames.filter((name) => name !== preferred);
        if (remaining.length) {
            prefetchAllPointers([], remaining).catch((err) => {
                console.warn('[ExplorerAPI] Background bookmark prefetch failed:', err);
            });
        }

        return { cursor_id: taskCursorId, bookmark: preferred };
    }

    async function _finalizeSearchTask(taskData, initialCursorId, options = {}) {
        let resolvedCursorId = initialCursorId;
        const bootstrap = taskData?.context?.cursor_bootstrap;
        const hasBootstrap = !!(bootstrap && bootstrap.bookmarks && Object.keys(bootstrap.bookmarks).length);
        const cursorChanged = !!(taskData?.cursor_id && taskData.cursor_id !== initialCursorId);

        if (hasBootstrap || cursorChanged) {
            const applied = await applyPreWarmedTaskCursor(taskData, {
                switchTab: options.navigateBookmarks !== false,
                bookmark: options.bookmark,
            });
            if (applied?.cursor_id) {
                resolvedCursorId = applied.cursor_id;
            }
        } else if ((taskData?.search_result?.total_entities_loaded || 0) > 0) {
            await refreshGraph(true);
        }

        return resolvedCursorId;
    }
    
    function setApiKey(apiKey) {
        if (apiKey && apiKey.trim()) {
            apiState.apiKey = apiKey.trim();
            apiState.authMethod = 'apikey';
            // Persist to session storage
            sessionStorage.setItem('explorerApiKey', apiState.apiKey);
            console.log('[ExplorerAPI] API key set and saved');
            updateApiKeyButtonVisibility();
            return true;
        }
        return false;
    }
    
    function clearApiKey() {
        apiState.apiKey = null;
        if (apiState.authMethod === 'apikey') {
            apiState.authMethod = null;
        }
        sessionStorage.removeItem('explorerApiKey');
        console.log('[ExplorerAPI] API key cleared');
        updateApiKeyButtonVisibility();
    }
    
    function hasAuthentication() {
        return apiState.authMethod !== null;
    }
    
    function getAuthHeaders() {
        const headers = {};
        if (apiState.authMethod === 'bearer' && apiState.authToken) {
            headers['Authorization'] = `Bearer ${apiState.authToken}`;
        } else if (apiState.authMethod === 'apikey' && apiState.apiKey) {
            headers['X-API-Key'] = apiState.apiKey;
        }
        // For cookie auth, no headers needed - credentials: 'include' handles it
        return headers;
    }

    function shouldIncludeCredentials() {
        // Include credentials for cookie-based auth (MCP UI sessions)
        return apiState.authMethod === 'cookie';
    }
    
    function updateApiKeyButtonVisibility() {
        // API key button removed from toolbar - this is now a no-op
        // Auth status is logged at init time
    }
    
    function promptForApiKey() {
        // API key prompt disabled - uses bearer token auth only
        console.log('[ExplorerAPI] API key prompt disabled - uses bearer auth');
        return false;
    }
    
    function showConfigDialog() {
        // Config dialog disabled - uses bearer token auth only
        console.log('[ExplorerAPI] Config dialog disabled');
    }
    
    function hideConfigDialog() {
        // No-op - dialog removed
    }
    
    function saveConfigFromDialog() {
        // No-op - dialog removed
    }

    // ============================================
    // Build API URL
    // ============================================
    function buildGraphDataUrl() {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined || 
            !apiState.workspaceId || !apiState.cursorId) {
            console.warn('[ExplorerAPI] Missing required parameters for API call');
            return null;
        }

        // Build URL: {apiEndpoint}/workspaces/{workspaceId}/cursors/{cursorId}/graph/data
        let url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/cursors/${apiState.cursorId}/graph/data`;

        const params = [];
        if (apiState.instanceId) {
            params.push(`instance_id=${encodeURIComponent(apiState.instanceId)}`);
        }
        if (apiState.activeNodeTypeFilter) {
            params.push(`include_node_type=${encodeURIComponent(apiState.activeNodeTypeFilter)}`);
        }
        if (apiState.activeEdgeNameFilter) {
            params.push(`include_edge_name=${encodeURIComponent(apiState.activeEdgeNameFilter)}`);
        }
        if (apiState.entrypointTaxonomyId) {
            params.push(`entrypoint_taxonomy_id=${encodeURIComponent(apiState.entrypointTaxonomyId)}`);
            if (apiState.trimFilter) {
                params.push('trim=true');
            }
        }
        if (apiState.extendedEdges) {
            params.push('extended=true');
        }
        if (typeof apiState.maxNodes === 'number' && apiState.maxNodes > 0) {
            params.push(`max_nodes=${apiState.maxNodes}`);
        }
        // Server-side layout parameter
        if (apiState.layoutAlgorithm && apiState.layoutAlgorithm !== 'none') {
            params.push(`layout=${encodeURIComponent(apiState.layoutAlgorithm)}`);
        } else if (apiState.layoutAlgorithm === 'none') {
            params.push('layout=none');
        } else if (apiState.autoLayout) {
            params.push('layout=auto');
        }
        // Layout tuning config (scale, k, iterations, seed)
        if (apiState.layoutConfig && typeof apiState.layoutConfig === 'object') {
            params.push(`layout_config=${encodeURIComponent(JSON.stringify(apiState.layoutConfig))}`);
        }
        if (params.length > 0) {
            url += '?' + params.join('&');
        }

        return url;
    }

    // ============================================
    // Instance Refresh (Rebuild Graph)
    // ============================================

    /**
     * Build URL for the instance refresh (rebuild) endpoint.
     * POST /workspaces/{workspaceId}/instances/{instanceId}/refresh
     */
    function buildRefreshInstanceUrl() {
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined ||
            !apiState.workspaceId) {
            console.warn('[ExplorerAPI] Missing parameters for instance refresh');
            return null;
        }
        const instId = apiState.instanceId || 'default';
        return `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/instances/${instId}/refresh?force_rebuild=true`;
    }

    /**
     * Rebuild the workspace graph end-to-end:
     *   1. Call POST /instances/{id}/refresh?force_rebuild=true
     *   2. Refresh the cursor (replay FQL history on the rebuilt instance)
     *   3. Invalidate all client-side caches
     *   4. Re-fetch graph data + side panels
     *
     * The cursor's FQL history is preserved and replayed so the user's
     * exploration context (aliases, selections, checkpoints) is maintained.
     *
     * Shows the loading indicator for the entire duration.
     * @returns {Promise<boolean>} true on success
     */
    async function rebuildGraph() {
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot rebuild');
            return false;
        }

        const refreshUrl = buildRefreshInstanceUrl();
        if (!refreshUrl) return false;

        apiState.isLoading = true;
        showLoadingIndicator(true, 'rebuild');
        console.log('[ExplorerAPI] Rebuilding graph …');

        try {
            // Step 1 — Trigger server-side rebuild
            const fetchOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders()
                }
            };
            if (shouldIncludeCredentials()) fetchOptions.credentials = 'include';

            const response = await fetch(refreshUrl, fetchOptions);
            if (!response.ok) {
                let detail = response.statusText;
                try { const b = await response.json(); detail = b.detail || detail; } catch (_) {}
                throw new Error(`Rebuild failed: HTTP ${response.status} — ${detail}`);
            }
            console.log('[ExplorerAPI] Server rebuild complete');

            // Step 2 — Refresh cursor (replay FQL history on rebuilt instance)
            try {
                const refreshResult = await refreshMeCursor();
                const replayed = refreshResult ? refreshResult.replayed || 0 : 0;
                const skipped = refreshResult ? refreshResult.skipped || 0 : 0;
                console.log(`[ExplorerAPI] Cursor refreshed after rebuild (replayed=${replayed}, skipped=${skipped})`);
                if (refreshResult && refreshResult.errors && refreshResult.errors.length > 0) {
                    console.warn('[ExplorerAPI] Some statements could not be replayed:', refreshResult.errors);
                }
            } catch (e) {
                console.warn('[ExplorerAPI] Cursor refresh failed, falling back to reset:', e.message);
                // Fallback: reset cursor if refresh endpoint is unavailable
                try {
                    await resetMeCursor();
                    console.log('[ExplorerAPI] Cursor reset after rebuild (fallback)');
                } catch (resetErr) {
                    console.warn('[ExplorerAPI] Cursor reset warning:', resetErr.message);
                }
            }

            // Step 3 — Invalidate all client-side caches
            apiState.lastDataHash = null;
            apiState.aliases = [];
            if (apiState._edgeRevealTimer) {
                clearTimeout(apiState._edgeRevealTimer);
                apiState._edgeRevealTimer = null;
            }

            // Step 4 — Re-fetch fresh graph data
            const graphData = await fetchGraphData();
            if (graphData && !graphData.error) {
                updateGraph(graphData, true);  // force update
                _refreshSideData();
                if (typeof GraphExplorer !== 'undefined' && GraphExplorer.checkEmptyState) {
                    GraphExplorer.checkEmptyState();
                }
                console.log('[ExplorerAPI] Graph rebuild + reload complete');
                return true;
            }

            console.warn('[ExplorerAPI] Rebuild succeeded but graph fetch returned no data');
            return false;
        } catch (error) {
            console.error('[ExplorerAPI] Rebuild failed:', error);
            if (typeof showWarningNotification === 'function' || showWarningNotification) {
                showWarningNotification('Graph rebuild failed: ' + error.message);
            }
            return false;
        } finally {
            apiState.isLoading = false;
            showLoadingIndicator(false);
        }
    }

    // ============================================
    // Legend Filter Controls
    // ============================================

    /**
     * Set the active node type filter for /graph/data requests.
     * Pass null to clear.
     * @param {string|null} nodeTypes - Comma-separated node types/labels, or null to clear
     */
    function setNodeTypeFilter(nodeTypes) {
        const prev = apiState.activeNodeTypeFilter;
        apiState.activeNodeTypeFilter = nodeTypes || null;
        // Force a data hash reset so the next refreshGraph detects the change
        apiState.lastDataHash = null;
        console.log('[ExplorerAPI] Node type filter:', prev, '->', apiState.activeNodeTypeFilter);
    }

    /**
     * Set the extra edge name filter for /graph/data requests.
     * Pass null to clear.
     * @param {string|null} edgeNames - Comma-separated edge names, or null to clear
     */
    function setEdgeNameFilter(edgeNames) {
        const prev = apiState.activeEdgeNameFilter;
        apiState.activeEdgeNameFilter = edgeNames || null;
        apiState.lastDataHash = null;
        console.log('[ExplorerAPI] Edge name filter:', prev, '->', apiState.activeEdgeNameFilter);
    }

    function setEntrypointTaxonomyId(taxonomyId) {
        const prev = apiState.entrypointTaxonomyId;
        apiState.entrypointTaxonomyId = taxonomyId || null;
        apiState.lastDataHash = null;
        console.log('[ExplorerAPI] Entrypoint taxonomy:', prev, '->', apiState.entrypointTaxonomyId);
    }

    function setTrimFilter(enabled) {
        const nextValue = !!enabled;
        const prev = apiState.trimFilter;
        apiState.trimFilter = nextValue;
        apiState.lastDataHash = null;
        console.log('[ExplorerAPI] Trim filter:', prev, '->', apiState.trimFilter);
    }

    function getActiveFilters() {
        return {
            nodeType: apiState.activeNodeTypeFilter,
            edgeName: apiState.activeEdgeNameFilter,
            entrypointTaxonomyId: apiState.entrypointTaxonomyId,
            trim: apiState.trimFilter
        };
    }

    function clearAllFilters() {
        apiState.activeNodeTypeFilter = null;
        apiState.activeEdgeNameFilter = null;
        apiState.lastDataHash = null;
        console.log('[ExplorerAPI] All legend filters cleared');
    }

    // ============================================
    // Aggregation & Extended Edge Controls
    // ============================================

    /**
     * Toggle extended (auto-discovered) edges on/off.
     * @param {boolean} enabled
     */
    function setExtendedEdges(enabled) {
        apiState.extendedEdges = !!enabled;
        apiState.lastDataHash = null;
        console.log('[ExplorerAPI] Extended edges:', apiState.extendedEdges);
    }

    /**
     * Set the aggregation threshold (max individual nodes).
     * @param {number} threshold  0 = disabled
     */
    function setMaxNodes(threshold) {
        apiState.maxNodes = Math.max(0, parseInt(threshold, 10) || 0);
        apiState.lastDataHash = null;
        console.log('[ExplorerAPI] Max nodes:', apiState.maxNodes);
    }

    /**
     * Check if a vis.js node is a synthetic aggregate (legacy — always false).
     * @param {object} nodeData
     * @returns {boolean}
     */
    function isAggregateNode(nodeData) {
        return false;
    }

    /**
     * Explode a synthetic aggregate node — no-op (diamond aggregation removed).
     * @param {string} syntheticId  The aggregate node's id
     * @returns {Promise<boolean>}
     */
    async function explodeAggregate(syntheticId) {
        console.warn('[ExplorerAPI] explodeAggregate is a no-op — diamond aggregation removed');
        return false;
    }

    /**
     * Get current aggregation metadata — always null (aggregation removed).
     * @returns {null}
     */
    function getAggregationMeta() {
        return null;
    }

    // ============================================
    // Pointer-Based Graph Data (checkpoint / bookmark)
    // ============================================

    /**
     * Build a graph/data URL that targets a specific pointer (checkpoint or bookmark).
     * @param {'checkpoint'|'bookmark'} pointerType
     * @param {string} pointerName
     * @returns {string|null}
     */
    function buildPointerGraphDataUrl(pointerType, pointerName) {
        const base = buildGraphDataUrl();
        if (!base) return null;
        const sep = base.includes('?') ? '&' : '?';
        return `${base}${sep}${pointerType}=${encodeURIComponent(pointerName)}`;
    }

    /**
     * Fetch graph/data for a single checkpoint or bookmark without modifying
     * the cursor's live state. The response shape is identical to the normal
     * graph/data response but carries ``meta.pointer`` indicating the source.
     *
     * @param {'checkpoint'|'bookmark'} pointerType
     * @param {string} pointerName
     * @returns {Promise<object>} graph data or { error }
     */
    async function fetchGraphDataForPointer(pointerType, pointerName, options = {}) {
        if (!options.bypassCache) {
            const cached = getCachedPointerGraph(pointerType, pointerName);
            if (cached) {
                console.log(`[ExplorerAPI] Cache hit ${pointerType}:${pointerName}`);
                return cached;
            }
        }

        if (!hasAuthentication()) {
            const embeddedPointerGraph = getStandalonePointerGraph(pointerType, pointerName);
            if (embeddedPointerGraph) {
                console.log(`[ExplorerAPI] Loaded embedded ${pointerType}:${pointerName} graph data`);
                cachePointerGraph(pointerType, pointerName, embeddedPointerGraph);
                return embeddedPointerGraph;
            }
        }
        if (!hasAuthentication()) {
            return { error: 'Authentication required', nodes: [], edges: [] };
        }
        const url = buildPointerGraphDataUrl(pointerType, pointerName);
        if (!url) return { error: 'Missing API configuration', nodes: [], edges: [] };

        try {
            const fetchOptions = {
                method: 'GET',
                headers: { 'Accept': 'application/json', ...getAuthHeaders() }
            };
            if (shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }
            const response = await fetch(url, fetchOptions);
            if (!response.ok) {
                let detail = response.statusText;
                try { const b = await response.json(); detail = b.detail || detail; } catch (_) {}
                throw new Error(`HTTP ${response.status}: ${detail}`);
            }
            const data = await response.json();
            console.log(`[ExplorerAPI] Fetched ${pointerType}:${pointerName} graph data:`,
                { nodes: data.nodes?.length || 0, edges: data.edges?.length || 0 });
            cachePointerGraph(pointerType, pointerName, data);
            return data;
        } catch (error) {
            console.error(`[ExplorerAPI] Failed to fetch ${pointerType}:${pointerName}:`, error);
            return { error: error.message, nodes: [], edges: [] };
        }
    }

    /**
     * Fetch graph/data for ALL checkpoints and bookmarks in parallel.
     * Returns a map keyed by ``"checkpoint:<name>"`` or ``"bookmark:<name>"``.
     *
     * @param {string[]} checkpointNames
     * @param {string[]} bookmarkNames
     * @returns {Promise<Record<string, object>>}
     */
    async function prefetchAllPointers(checkpointNames = [], bookmarkNames = []) {
        if (!hasAuthentication()) {
            const pointerGraphs = apiState.standaloneSnapshot && apiState.standaloneSnapshot.pointerGraphs;
            if (pointerGraphs) {
                const map = {};
                for (const name of checkpointNames) {
                    const key = `checkpoint:${name}`;
                    if (pointerGraphs[key]) {
                        const data = cloneStandalonePayload(pointerGraphs[key], { nodes: [], edges: [] });
                        map[key] = data;
                        cachePointerGraph('checkpoint', name, data);
                    }
                }
                for (const name of bookmarkNames) {
                    const key = `bookmark:${name}`;
                    if (pointerGraphs[key]) {
                        const data = cloneStandalonePayload(pointerGraphs[key], { nodes: [], edges: [] });
                        map[key] = data;
                        cachePointerGraph('bookmark', name, data);
                    }
                }
                console.log(`[ExplorerAPI] Loaded ${Object.keys(map).length} embedded pointer snapshot(s)`);
                return map;
            }
        }
        const tasks = [];
        for (const name of checkpointNames) {
            tasks.push(
                fetchGraphDataForPointer('checkpoint', name)
                    .then(data => ({ key: `checkpoint:${name}`, data }))
            );
        }
        for (const name of bookmarkNames) {
            tasks.push(
                fetchGraphDataForPointer('bookmark', name)
                    .then(data => ({ key: `bookmark:${name}`, data }))
            );
        }
        const results = await Promise.all(tasks);
        const map = {};
        for (const { key, data } of results) {
            map[key] = data;
        }
        console.log(`[ExplorerAPI] Prefetched ${Object.keys(map).length} pointer(s)`);
        return map;
    }

    function buildGraphDocumentsUrl() {
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined ||
            !apiState.workspaceId || !apiState.cursorId) {
            return null;
        }
        let url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/cursors/${apiState.cursorId}/graph/documents`;
        if (apiState.instanceId) {
            url += `?instance_id=${encodeURIComponent(apiState.instanceId)}`;
        }
        return url;
    }

    async function fetchGraphDocuments(signal) {
        if (!hasAuthentication()) {
            const embeddedDocuments = getStandaloneDocumentsData();
            if (embeddedDocuments) {
                console.log('[ExplorerAPI] Loaded embedded graph documents:', embeddedDocuments.meta || {});
                return embeddedDocuments;
            }
        }
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot fetch documents');
            return { documents: [], meta: {} };
        }

        const url = buildGraphDocumentsUrl();
        if (!url) {
            return { documents: [], meta: {} };
        }

        try {
            const fetchOptions = {
                method: 'GET',
                headers: { 'Accept': 'application/json', ...getAuthHeaders() }
            };
            if (shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }
            if (signal) {
                fetchOptions.signal = signal;
            }

            const response = await fetch(url, fetchOptions);
            if (!response.ok) {
                let errorDetail = response.statusText;
                try { const b = await response.json(); errorDetail = b.detail || errorDetail; } catch (_) {}
                throw new Error(`HTTP ${response.status}: ${errorDetail}`);
            }

            const data = await response.json();
            console.log('[ExplorerAPI] Fetched graph documents:', data.meta);
            return data;
        } catch (error) {
            console.error('[ExplorerAPI] Failed to fetch graph documents:', error);
            return { documents: [], meta: {} };
        }
    }

    async function fetchCursorState(signal) {
        if (!hasAuthentication()) {
            const embeddedState = getStandaloneCursorState();
            if (embeddedState) {
                console.log('[ExplorerAPI] Loaded embedded cursor state');
                return embeddedState;
            }
        }
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot fetch state');
            return null;
        }

        const url = buildCursorStateUrl(apiState.cursorId || 'me');
        if (!url) return null;

        try {
            const fetchOptions = {
                method: 'GET',
                headers: { 'Accept': 'application/json', ...getAuthHeaders() }
            };
            if (shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }
            if (signal) {
                fetchOptions.signal = signal;
            }

            const response = await fetch(url, fetchOptions);
            if (!response.ok) {
                let errorDetail = response.statusText;
                try { const b = await response.json(); errorDetail = b.detail || errorDetail; } catch (_) {}
                throw new Error(`HTTP ${response.status}: ${errorDetail}`);
            }

            const data = await response.json();
            console.log('[ExplorerAPI] Fetched cursor state');
            return data;
        } catch (error) {
            console.error('[ExplorerAPI] Failed to fetch cursor state:', error);
            return null;
        }
    }

    async function fetchGraphData(signal) {
        if (!hasAuthentication()) {
            const embeddedGraphData = getStandaloneGraphData();
            if (embeddedGraphData && !embeddedGraphData.error) {
                apiState.lastUpdate = new Date();
                console.log('[ExplorerAPI] Loaded embedded standalone graph data:', {
                    nodes: embeddedGraphData.nodes?.length || 0,
                    edges: embeddedGraphData.edges?.length || 0
                });
                return embeddedGraphData;
            }
        }
        // Check if we have authentication (cookie auth is always valid)
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot fetch data');
            showConfigDialog();
            return { error: 'Authentication required', nodes: [], edges: [] };
        }
        
        const url = buildGraphDataUrl();
        if (!url) {
            return { error: 'Missing API configuration', nodes: [], edges: [] };
        }

        apiState.isLoading = true;
        showLoadingIndicator(true);

        try {
            const fetchOptions = {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders()  // Add Bearer or API Key headers (empty for cookie auth)
                }
            };
            
            // Include credentials for cookie-based authentication (MCP UI sessions)
            if (shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }

            // Attach abort signal when provided (allows refreshGraph to cancel
            // stale in-flight requests when a newer refresh is triggered).
            if (signal) {
                fetchOptions.signal = signal;
            }

            const response = await fetch(url, fetchOptions);

            if (!response.ok) {
                // Try to get error detail from response
                let errorDetail = response.statusText;
                try {
                    const errorBody = await response.json();
                    errorDetail = errorBody.detail || errorBody.message || errorDetail;
                } catch (e) {
                    // Ignore JSON parse error
                }
                throw new Error(`HTTP ${response.status}: ${errorDetail}`);
            }

            const data = await response.json();
            apiState.lastUpdate = new Date();
            
            console.log('[ExplorerAPI] Fetched graph data:', {
                nodes: data.nodes?.length || 0,
                edges: data.edges?.length || 0
            });

            return data;
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('[ExplorerAPI] Graph data fetch aborted (superseded by newer request)');
                return { error: 'Aborted', nodes: [], edges: [] };
            }
            console.error('[ExplorerAPI] Failed to fetch graph data:', error);
            return { error: error.message, nodes: [], edges: [] };
        } finally {
            apiState.isLoading = false;
            showLoadingIndicator(false);
        }
    }

    // ============================================
    // Hash Computation for Change Detection
    // ============================================
    function computeDataHash(graphData) {
        // Create a stable hash based on node/edge IDs and key properties
        // Sort everything to ensure consistent hashing regardless of order
        const nodeData = (graphData.nodes || []).map(n => ({
            id: n.id,
            label: n.label || '',
            group: n.group || ''
        })).sort((a, b) => a.id.localeCompare(b.id));
        
        const edgeData = (graphData.edges || []).map(e => ({
            id: e.id || `${e.from}-${e.to}`,
            from: e.from,
            to: e.to
        })).sort((a, b) => a.id.localeCompare(b.id));
        
        const aliasData = (graphData.aliases || []).slice().sort();
        
        // Create a stable string representation
        const str = JSON.stringify({ nodes: nodeData, edges: edgeData, aliases: aliasData });
        
        // Simple hash function (djb2)
        let hash = 5381;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) + hash) + str.charCodeAt(i);
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash.toString(16);
    }

    function clearDeferredEdgeReveal() {
        apiState._edgeRevealGeneration += 1;
        if (apiState._edgeRevealTimer) {
            clearTimeout(apiState._edgeRevealTimer);
            apiState._edgeRevealTimer = null;
        }
        if (typeof apiState._edgeRevealCleanup === 'function') {
            try {
                apiState._edgeRevealCleanup();
            } catch (error) {
                console.warn('[ExplorerAPI] Failed to clean up edge reveal listeners:', error);
            }
        }
        apiState._edgeRevealCleanup = null;
        apiState._deferredEdgeIds = null;
    }

    function clearDeferredNodeLabelReveal() {
        apiState._nodeLabelRevealGeneration += 1;
        if (apiState._nodeLabelRevealTimer) {
            clearTimeout(apiState._nodeLabelRevealTimer);
            apiState._nodeLabelRevealTimer = null;
        }
        if (typeof apiState._nodeLabelRevealCleanup === 'function') {
            try {
                apiState._nodeLabelRevealCleanup();
            } catch (error) {
                console.warn('[ExplorerAPI] Failed to clean up node label reveal listeners:', error);
            }
        }
        apiState._nodeLabelRevealCleanup = null;
        apiState._deferredNodeLabelIds = null;
    }

    function clearDeferredInteractionResume() {
        apiState._interactionResumeGeneration += 1;
        if (apiState._interactionResumeTimer) {
            clearTimeout(apiState._interactionResumeTimer);
            apiState._interactionResumeTimer = null;
        }
        if (typeof apiState._interactionResumeCleanup === 'function') {
            try {
                apiState._interactionResumeCleanup();
            } catch (error) {
                console.warn('[ExplorerAPI] Failed to clean up interaction resume listeners:', error);
            }
        }
        apiState._interactionResumeCleanup = null;
    }

    function isLargeGraphProfilePinned() {
        return apiState.performanceMode === 'large';
    }

    function isInteractionDeferred() {
        return !!apiState.interactionDeferred;
    }

    function setDeferredInteractionState(enabled) {
        apiState.interactionDeferred = !!enabled;
        if (typeof ExplorerHoverMenu !== 'undefined' && ExplorerHoverMenu.setInteractionsDeferred) {
            ExplorerHoverMenu.setInteractionsDeferred(apiState.interactionDeferred);
        }
    }

    function setNetworkInteractionState(network, deferred) {
        if (!network) {
            setDeferredInteractionState(deferred);
            return;
        }
        network._suppressFit = true;
        network.setOptions({
            interaction: {
                hover: !deferred,
                hideEdgesOnZoom: network.options && network.options.interaction ? network.options.interaction.hideEdgesOnZoom : false,
                hoverConnectedEdges: !deferred,
                tooltipDelay: deferred ? 1200 : 200,
            },
        });
        network._suppressFit = false;
        setDeferredInteractionState(deferred);
    }

    function updatePerformanceModeButton() {
        if (typeof ExplorerToolbar !== 'undefined' && ExplorerToolbar.setButtonActive) {
            ExplorerToolbar.setButtonActive('btn-performance-mode', isLargeGraphProfilePinned());
        }
    }

    function applyChunkedDataSetUpdates(dataSet, updates, chunkSize, shouldContinue, onDone) {
        if (!dataSet || !updates || updates.length === 0) {
            if (typeof onDone === 'function') {
                onDone(0);
            }
            return;
        }

        var index = 0;
        function runChunk() {
            if (typeof shouldContinue === 'function' && !shouldContinue()) {
                return;
            }
            var nextIndex = Math.min(index + chunkSize, updates.length);
            dataSet.update(updates.slice(index, nextIndex));
            index = nextIndex;
            if (index < updates.length) {
                requestAnimationFrame(runChunk);
            } else if (typeof onDone === 'function') {
                onDone(updates.length);
            }
        }
        requestAnimationFrame(runChunk);
    }

    function getViewportBounds(network) {
        if (!network || !network.body || !network.body.view || !network.canvas || !network.canvas.frame || !network.canvas.frame.canvas) {
            return null;
        }
        var canvas = network.canvas.frame.canvas;
        var scale = network.getScale ? network.getScale() : (network.body.view.scale || 1);
        var center = network.getViewPosition ? network.getViewPosition() : { x: 0, y: 0 };
        var halfWidth = (canvas.clientWidth || canvas.width || 0) / (2 * Math.max(scale, 0.0001));
        var halfHeight = (canvas.clientHeight || canvas.height || 0) / (2 * Math.max(scale, 0.0001));
        return {
            left: center.x - halfWidth,
            right: center.x + halfWidth,
            top: center.y - halfHeight,
            bottom: center.y + halfHeight,
        };
    }

    function edgeViewportRank(network, edge) {
        if (!network || !edge) {
            return 2;
        }
        var bounds = getViewportBounds(network);
        if (!bounds) {
            return 2;
        }
        var positions = network.getPositions([edge.from, edge.to]);
        var fromPos = positions[edge.from];
        var toPos = positions[edge.to];
        if (!fromPos || !toPos) {
            return 2;
        }

        function inBounds(pos) {
            return pos.x >= bounds.left && pos.x <= bounds.right && pos.y >= bounds.top && pos.y <= bounds.bottom;
        }

        var fromIn = inBounds(fromPos);
        var toIn = inBounds(toPos);
        if (fromIn && toIn) {
            return 0;
        }
        if (fromIn || toIn) {
            return 1;
        }
        return 2;
    }

    function selectViewportAwareEdgeChunk(network, edges, edgeIds, chunkSize) {
        var visible = [];
        var adjacent = [];
        var offscreen = [];

        for (var i = 0; i < edgeIds.length; i++) {
            var edgeId = edgeIds[i];
            var edge = edges.get(edgeId);
            if (!edge || !edge.hidden) {
                continue;
            }
            var rank = edgeViewportRank(network, edge);
            if (rank === 0) {
                visible.push(edgeId);
            } else if (rank === 1) {
                adjacent.push(edgeId);
            } else {
                offscreen.push(edgeId);
            }
        }

        var ordered = visible.concat(adjacent, offscreen);
        return ordered.slice(0, chunkSize);
    }

    function scheduleDeferredInteractionResume(network, maxDelayMs) {
        if (!network || !apiState.largeGraphProfileActive) {
            clearDeferredInteractionResume();
            setNetworkInteractionState(network, false);
            return;
        }

        clearDeferredInteractionResume();
        var generation = apiState._interactionResumeGeneration;

        function finalizeResume(reason) {
            if (generation !== apiState._interactionResumeGeneration) {
                return;
            }
            clearDeferredInteractionResume();
            setNetworkInteractionState(network, false);
            console.log('[ExplorerAPI] Restored hover and tooltip interactions (' + reason + ')');
        }

        var onIterationsDone = function() {
            finalizeResume('stabilization-done');
        };
        var onStabilized = function() {
            finalizeResume('stabilized');
        };

        network.on('stabilizationIterationsDone', onIterationsDone);
        network.on('stabilized', onStabilized);
        apiState._interactionResumeCleanup = function() {
            network.off('stabilizationIterationsDone', onIterationsDone);
            network.off('stabilized', onStabilized);
        };
        apiState._interactionResumeTimer = setTimeout(function() {
            finalizeResume('timeout');
        }, maxDelayMs);
    }

    function chooseEdgeRevealPolicy(nodeCount, edgeCount) {
        var totalElements = nodeCount + edgeCount;
        var density = nodeCount > 0 ? (edgeCount / nodeCount) : 0;

        if (isLargeGraphProfilePinned()) {
            return {
                maxDelayMs: 7000,
                minDelayMs: 1200,
                revealProgress: 0.92,
            };
        }

        if (edgeCount < 600 || totalElements < 2500) {
            return null;
        }
        if (totalElements > 18000 || density >= 8) {
            return {
                maxDelayMs: 7000,
                minDelayMs: 1200,
                revealProgress: 0.92,
            };
        }
        if (totalElements > 9000 || density >= 5) {
            return {
                maxDelayMs: 4500,
                minDelayMs: 800,
                revealProgress: 0.85,
            };
        }
        return {
            maxDelayMs: 2500,
            minDelayMs: 500,
            revealProgress: 0.72,
        };
    }

    function chooseNodeLabelRevealPolicy(nodeCount, edgeCount) {
        var totalElements = nodeCount + edgeCount;
        if (isLargeGraphProfilePinned()) {
            return {
                maxDelayMs: 8000,
                minDelayMs: 1400,
                revealProgress: 0.95,
                chunkSize: 220,
            };
        }
        if (nodeCount < 400 || totalElements < 2500) {
            return null;
        }
        if (totalElements > 18000 || edgeCount / Math.max(nodeCount, 1) >= 8) {
            return {
                maxDelayMs: 8000,
                minDelayMs: 1400,
                revealProgress: 0.95,
                chunkSize: 220,
            };
        }
        if (totalElements > 9000 || edgeCount / Math.max(nodeCount, 1) >= 5) {
            return {
                maxDelayMs: 5200,
                minDelayMs: 900,
                revealProgress: 0.88,
                chunkSize: 320,
            };
        }
        return {
            maxDelayMs: 3200,
            minDelayMs: 600,
            revealProgress: 0.78,
            chunkSize: 480,
        };
    }

    function revealDeferredEdges(network, edges, reason) {
        if (!edges || !apiState._deferredEdgeIds || apiState._deferredEdgeIds.length === 0) {
            clearDeferredEdgeReveal();
            return 0;
        }

        var updates = [];
        var generation = apiState._edgeRevealGeneration;
        if (apiState._edgeRevealTimer) {
            clearTimeout(apiState._edgeRevealTimer);
            apiState._edgeRevealTimer = null;
        }
        if (typeof apiState._edgeRevealCleanup === 'function') {
            apiState._edgeRevealCleanup();
            apiState._edgeRevealCleanup = null;
        }
        for (var i = 0; i < apiState._deferredEdgeIds.length; i++) {
            var edgeId = apiState._deferredEdgeIds[i];
            var edge = edges.get(edgeId);
            if (edge && edge.hidden) {
                updates.push({ id: edgeId, hidden: false });
            }
        }

        if (updates.length > 0) {
            var pendingIds = updates.map(function(update) { return update.id; });
            var chunkSize = pendingIds.length > 5000 ? 300 : (pendingIds.length > 2000 ? 500 : 800);
            function runNextChunk() {
                if (generation !== apiState._edgeRevealGeneration) {
                    return;
                }
                var chunkIds = selectViewportAwareEdgeChunk(network, edges, pendingIds, chunkSize);
                if (!chunkIds.length) {
                    apiState._deferredEdgeIds = null;
                    console.log('[ExplorerAPI] Revealed ' + updates.length + ' deferred edges (' + reason + ')');
                    return;
                }
                var chunkUpdates = chunkIds.map(function(edgeId) { return { id: edgeId, hidden: false }; });
                edges.update(chunkUpdates);
                var chunkSet = new Set(chunkIds);
                pendingIds = pendingIds.filter(function(edgeId) { return !chunkSet.has(edgeId); });
                if (pendingIds.length > 0) {
                    requestAnimationFrame(runNextChunk);
                } else {
                    apiState._deferredEdgeIds = null;
                    console.log('[ExplorerAPI] Revealed ' + updates.length + ' deferred edges (' + reason + ')');
                }
            }
            requestAnimationFrame(runNextChunk);
            return updates.length;
        }

        apiState._deferredEdgeIds = null;
        return updates.length;
    }

    function revealDeferredNodeLabels(nodes, reason, policy) {
        if (!nodes || !apiState._deferredNodeLabelIds || apiState._deferredNodeLabelIds.length === 0) {
            clearDeferredNodeLabelReveal();
            return 0;
        }

        var generation = apiState._nodeLabelRevealGeneration;
        if (apiState._nodeLabelRevealTimer) {
            clearTimeout(apiState._nodeLabelRevealTimer);
            apiState._nodeLabelRevealTimer = null;
        }
        if (typeof apiState._nodeLabelRevealCleanup === 'function') {
            apiState._nodeLabelRevealCleanup();
            apiState._nodeLabelRevealCleanup = null;
        }

        var updates = [];
        for (var i = 0; i < apiState._deferredNodeLabelIds.length; i++) {
            var nodeId = apiState._deferredNodeLabelIds[i];
            var node = nodes.get(nodeId);
            if (node && node.hiddenLabel !== undefined) {
                updates.push({ id: nodeId, label: node.hiddenLabel, hiddenLabel: undefined });
            }
        }

        if (updates.length > 0) {
            applyChunkedDataSetUpdates(
                nodes,
                updates,
                (policy && policy.chunkSize) || 400,
                function() {
                    return generation === apiState._nodeLabelRevealGeneration &&
                        !(typeof ExplorerCore !== 'undefined' && ExplorerCore.state && ExplorerCore.state.nodeLabelsVisible === false);
                },
                function(count) {
                    if (generation !== apiState._nodeLabelRevealGeneration) {
                        return;
                    }
                    apiState._deferredNodeLabelIds = null;
                    console.log('[ExplorerAPI] Revealed ' + count + ' deferred node labels (' + reason + ')');
                }
            );
            return updates.length;
        }

        apiState._deferredNodeLabelIds = null;
        return 0;
    }

    function scheduleDeferredEdgeReveal(network, edges, edgeIds, policy) {
        if (!network || !edges || !edgeIds || edgeIds.length === 0 || !policy) {
            return;
        }

        clearDeferredEdgeReveal();
        apiState._deferredEdgeIds = edgeIds.slice();

        var startedAt = Date.now();
        var revealed = false;

        function finalizeReveal(reason, force, progress) {
            if (revealed) {
                return;
            }
            var elapsed = Date.now() - startedAt;
            if (!force) {
                if (elapsed < policy.minDelayMs) {
                    return;
                }
                if (typeof progress === 'number' && progress < policy.revealProgress) {
                    return;
                }
            }
            revealed = true;
            revealDeferredEdges(network, edges, reason);
        }

        var onProgress = function(params) {
            if (!params || !params.total) {
                return;
            }
            finalizeReveal('stabilization-progress', false, params.iterations / params.total);
        };
        var onIterationsDone = function() {
            finalizeReveal('stabilization-done', true);
        };
        var onStabilized = function() {
            finalizeReveal('stabilized', true);
        };

        network.on('stabilizationProgress', onProgress);
        network.on('stabilizationIterationsDone', onIterationsDone);
        network.on('stabilized', onStabilized);

        apiState._edgeRevealCleanup = function() {
            network.off('stabilizationProgress', onProgress);
            network.off('stabilizationIterationsDone', onIterationsDone);
            network.off('stabilized', onStabilized);
        };
        apiState._edgeRevealTimer = setTimeout(function() {
            finalizeReveal('timeout', true);
        }, policy.maxDelayMs);
    }

    function scheduleDeferredNodeLabelReveal(network, nodes, nodeIds, policy) {
        if (!network || !nodes || !nodeIds || nodeIds.length === 0 || !policy) {
            return;
        }

        clearDeferredNodeLabelReveal();
        apiState._deferredNodeLabelIds = nodeIds.slice();

        var startedAt = Date.now();
        var revealed = false;

        function finalizeReveal(reason, force, progress) {
            if (revealed) {
                return;
            }
            if (typeof ExplorerCore !== 'undefined' && ExplorerCore.state && ExplorerCore.state.nodeLabelsVisible === false) {
                clearDeferredNodeLabelReveal();
                return;
            }
            var elapsed = Date.now() - startedAt;
            if (!force) {
                if (elapsed < policy.minDelayMs) {
                    return;
                }
                if (typeof progress === 'number' && progress < policy.revealProgress) {
                    return;
                }
            }
            revealed = true;
            revealDeferredNodeLabels(nodes, reason, policy);
        }

        var onProgress = function(params) {
            if (!params || !params.total) {
                return;
            }
            finalizeReveal('stabilization-progress', false, params.iterations / params.total);
        };
        var onIterationsDone = function() {
            finalizeReveal('stabilization-done', true);
        };
        var onStabilized = function() {
            finalizeReveal('stabilized', true);
        };

        network.on('stabilizationProgress', onProgress);
        network.on('stabilizationIterationsDone', onIterationsDone);
        network.on('stabilized', onStabilized);

        apiState._nodeLabelRevealCleanup = function() {
            network.off('stabilizationProgress', onProgress);
            network.off('stabilizationIterationsDone', onIterationsDone);
            network.off('stabilized', onStabilized);
        };
        apiState._nodeLabelRevealTimer = setTimeout(function() {
            finalizeReveal('timeout', true);
        }, policy.maxDelayMs);
    }

    function applyGraphPerformanceProfile(network, totalElements, nodeCount, edgeCount) {
        if (!network) {
            return;
        }

        var density = nodeCount > 0 ? (edgeCount / nodeCount) : 0;
        var useLargeProfile = isLargeGraphProfilePinned() || totalElements > 12000 || density >= 6;
        var useMediumProfile = useLargeProfile || totalElements > 4500 || density >= 3;
        var currentSolver = (typeof ExplorerCore !== 'undefined' && ExplorerCore.state && ExplorerCore.state.currentSolver)
            || (typeof GraphExplorer !== 'undefined' && GraphExplorer.options && GraphExplorer.options.physics_solver)
            || 'forceAtlas2Based';
        var canAutoSelectSolver = currentSolver === 'forceAtlas2Based' || currentSolver === 'barnesHut';
        var solver = canAutoSelectSolver
            ? (useMediumProfile ? 'barnesHut' : 'forceAtlas2Based')
            : currentSolver;

        var physicsOptions = {
            stabilization: {
                enabled: true,
                iterations: useLargeProfile ? 35 : (useMediumProfile ? 55 : 90),
                updateInterval: 100,
                fit: false,
            },
            adaptiveTimestep: true,
            timestep: useLargeProfile ? 0.5 : (useMediumProfile ? 0.45 : 0.35),
            maxVelocity: useLargeProfile ? 24 : (useMediumProfile ? 32 : 40),
            minVelocity: useLargeProfile ? 1.8 : (useMediumProfile ? 1.2 : 0.75),
        };

        if (solver === 'barnesHut') {
            physicsOptions.solver = 'barnesHut';
            physicsOptions.barnesHut = useLargeProfile ? {
                theta: 0.9,
                gravitationalConstant: -3200,
                centralGravity: 0.18,
                springLength: 120,
                springConstant: 0.03,
                damping: 0.22,
                avoidOverlap: 0.1,
            } : {
                theta: 0.7,
                gravitationalConstant: -2600,
                centralGravity: 0.22,
                springLength: 110,
                springConstant: 0.04,
                damping: 0.16,
                avoidOverlap: 0.05,
            };
            if (typeof GraphExplorer !== 'undefined' && GraphExplorer.options) {
                GraphExplorer.options.physics_solver = 'barnesHut';
                GraphExplorer.options.physics_options = GraphExplorer.options.physics_options || {};
                GraphExplorer.options.physics_options.barnesHut = physicsOptions.barnesHut;
            }
            if (typeof ExplorerCore !== 'undefined' && ExplorerCore.state) {
                ExplorerCore.state.currentSolver = 'barnesHut';
            }
        }

        apiState.largeGraphProfileActive = !!useLargeProfile;
        network._suppressFit = true;
        network.setOptions({
            physics: physicsOptions,
            edges: {
                smooth: useMediumProfile ? false : {
                    enabled: true,
                    type: (typeof GraphExplorer !== 'undefined' && GraphExplorer.options && GraphExplorer.options.edge_smooth) || 'continuous',
                },
            },
            interaction: {
                hideEdgesOnZoom: useMediumProfile,
            },
        });
        network._suppressFit = false;
        setNetworkInteractionState(network, useLargeProfile);

        console.log('[ExplorerAPI] Applied graph performance profile:', {
            nodeCount: nodeCount,
            edgeCount: edgeCount,
            solver: solver,
            smoothEdges: !useMediumProfile,
            performanceMode: apiState.performanceMode,
        });
    }

    function setPerformanceMode(mode) {
        var normalized = mode === 'large' ? 'large' : 'auto';
        apiState.performanceMode = normalized;
        persistPerformanceMode(normalized);
        updatePerformanceModeButton();
        console.log('[ExplorerAPI] Performance mode set to:', normalized);
        return normalized;
    }

    function togglePerformanceMode() {
        return setPerformanceMode(apiState.performanceMode === 'large' ? 'auto' : 'large');
    }

    // ============================================
    // Update Graph with New Data
    // ============================================
    function updateGraph(graphData, forceUpdate = false) {
        if (!graphData || graphData.error) {
            console.error('[ExplorerAPI] Cannot update graph:', graphData?.error || 'No data');
            announceUpdate('Failed to update graph');
            return false;
        }
        
        // Always update aliases cache regardless of hash
        if (graphData.aliases) {
            apiState.aliases = graphData.aliases;
            updateAliasesList(graphData.aliases);
        }
        
        // Check hash to avoid unnecessary updates
        const newHash = computeDataHash(graphData);
        if (!forceUpdate && apiState.lastDataHash === newHash) {
            console.log('[ExplorerAPI] Data unchanged (hash match), skipping graph update');
            return true;  // Return true since data is valid, just unchanged
        }
        apiState.lastDataHash = newHash;

        const nodes = GraphExplorer.state.nodes;
        const edges = GraphExplorer.state.edges;
        const network = GraphExplorer.state.network;

        if (!nodes || !edges || !network) {
            console.error('[ExplorerAPI] Graph not initialized');
            return false;
        }

        // Store current view state to restore after update
        const viewPosition = network.getViewPosition();
        const scale = network.getScale();
        const selectedNodes = network.getSelectedNodes();
        const selectedEdges = network.getSelectedEdges();
        // Track whether the graph was empty before this update (first load vs. refresh)
        const wasEmpty = nodes.length === 0;

        // Determine if we should defer edge visibility for performance
        const nodeCount = graphData.nodes?.length || 0;
        const edgeCount = graphData.edges?.length || 0;
        const totalElements = nodeCount + edgeCount;
        const edgeRevealPolicy = chooseEdgeRevealPolicy(nodeCount, edgeCount);
        const nodeLabelRevealPolicy = chooseNodeLabelRevealPolicy(nodeCount, edgeCount);
        const interactionResumeDelay = Math.max(
            edgeRevealPolicy ? edgeRevealPolicy.maxDelayMs : 0,
            nodeLabelRevealPolicy ? nodeLabelRevealPolicy.maxDelayMs : 0,
            apiState.largeGraphProfileActive ? 5000 : 0
        );
        let deferEdges = !!edgeRevealPolicy;
        
        applyGraphPerformanceProfile(network, totalElements, nodeCount, edgeCount);
        scheduleDeferredInteractionResume(network, interactionResumeDelay || 1000);

        try {
            // Pre-check: will client-side clustering fire after data load?
            // If yes, disable physics BEFORE adding nodes so vis.js doesn't
            // run expensive per-add physics ticks on 5000+ nodes.
            const preClusterCheck = typeof ExplorerClustering !== 'undefined' &&
                ExplorerClustering.autoClusterEnabled &&
                nodeCount >= (ExplorerClustering.clusterThreshold || 500);
            if (preClusterCheck && typeof GraphExplorer.disablePhysics === 'function') {
                GraphExplorer.disablePhysics();
            }

            // Clear and re-add all nodes and edges
            nodes.clear();
            edges.clear();

            clearDeferredEdgeReveal();
            clearDeferredNodeLabelReveal();

            if (graphData.nodes && graphData.nodes.length > 0) {
                var visibleNodeLabels = !(typeof ExplorerCore !== 'undefined' && ExplorerCore.state && ExplorerCore.state.nodeLabelsVisible === false);
                var deferredNodeLabelIds = [];
                var nodePayload = graphData.nodes;
                if (visibleNodeLabels && nodeLabelRevealPolicy) {
                    nodePayload = graphData.nodes.map(function(node) {
                        if (node.label !== undefined && node.label !== '' && node.hiddenLabel === undefined) {
                            deferredNodeLabelIds.push(node.id);
                            return Object.assign({}, node, {
                                hiddenLabel: node.label,
                                label: ''
                            });
                        }
                        return node;
                    });
                }
                nodes.add(nodePayload);
                if (deferredNodeLabelIds.length > 0) {
                    scheduleDeferredNodeLabelReveal(network, nodes, deferredNodeLabelIds, nodeLabelRevealPolicy);
                    console.log('[ExplorerAPI] Large graph detected (' + nodeCount + ' nodes) — node labels deferred until layout settles');
                }
            }

            if (graphData.edges && graphData.edges.length > 0) {
                if (preClusterCheck) {
                    // *** FAST PATH ***  Don't add edges to vis.js at all.
                    // Clustering groups nodes by attribute (no edges needed)
                    // and the clustered view has ~10 blobs with 0 inter-edges.
                    // Keeping 20K+ edges out of the DataSet avoids O(E)
                    // per-frame overhead in vis.js rendering and physics.
                    apiState._storedEdges = graphData.edges;
                    console.log('[ExplorerAPI] Edges stored outside vis.js (' + edgeCount + ') — will inject on cluster expand');
                } else if (deferEdges) {
                    apiState._storedEdges = null;
                    var deferredEdgeIds = [];
                    var hiddenEdges = graphData.edges.map(function(e, index) {
                        var hiddenEdge = Object.assign({}, e, { hidden: true });
                        if (hiddenEdge.id === undefined || hiddenEdge.id === null || hiddenEdge.id === '') {
                            hiddenEdge.id = hiddenEdge.from + '->' + hiddenEdge.to + '#' + index;
                        }
                        deferredEdgeIds.push(hiddenEdge.id);
                        return hiddenEdge;
                    });
                    edges.add(hiddenEdges);
                    if (deferredEdgeIds.length > 0) {
                        scheduleDeferredEdgeReveal(network, edges, deferredEdgeIds, edgeRevealPolicy);
                    }
                    console.log('[ExplorerAPI] Large graph detected (' + nodeCount + ' nodes, ' + edgeCount + ' edges) — edge paint deferred until layout settles');
                } else {
                    apiState._storedEdges = null;
                    edges.add(graphData.edges);
                }
            } else {
                apiState._storedEdges = null;
            }

            // Restore view position
            network.moveTo({
                position: viewPosition,
                scale: scale,
                animation: false
            });

            // Restore selection if nodes still exist
            const existingSelectedNodes = selectedNodes.filter(id => nodes.get(id) !== null);
            const existingSelectedEdges = selectedEdges.filter(id => edges.get(id) !== null);
            
            if (existingSelectedNodes.length > 0 || existingSelectedEdges.length > 0) {
                network.setSelection({
                    nodes: existingSelectedNodes,
                    edges: existingSelectedEdges
                });
            }

            // Store original colors for new nodes/edges
            if (typeof GraphExplorer.storeOriginalEdgeColors === 'function') {
                GraphExplorer.storeOriginalEdgeColors();
            }

            // Apply default color mapping by resource_name first, which populates
            // state.legendColorMap — then buildInitialLegend reads the correct colors
            if (typeof GraphExplorer.setColorByAttribute === 'function') {
                const colorAttr = GraphExplorer.state?.currentColorAttribute || 'resource_name';
                GraphExplorer.setColorByAttribute(colorAttr);
            }

            // Apply edge color palette by label (skip if edges stored externally)
            if (!preClusterCheck && typeof GraphExplorer.setEdgeColorByLabel === 'function') {
                GraphExplorer.setEdgeColorByLabel();
            }

            // Hide edge labels if they are disabled (default)
            // Skip if edges stored externally — no edges in DataSet
            if (!preClusterCheck && GraphExplorer.state && !GraphExplorer.state.edgeLabelsVisible) {
                var allEdges = edges.get();
                var labelUpdates = [];
                allEdges.forEach(function(edge) {
                    if (edge.label !== undefined && edge.label !== '') {
                        labelUpdates.push({
                            id: edge.id,
                            hiddenLabel: edge.label,
                            hiddenFontSize: edge.font?.size || 12,
                            font: Object.assign({}, edge.font, { size: 0 })
                        });
                    }
                });
                if (labelUpdates.length > 0) {
                    edges.update(labelUpdates);
                }
            }

            // Rebuild legend for new data (reads state.legendColorMap populated above)
            if (typeof GraphExplorer.buildInitialLegend === 'function') {
                GraphExplorer.buildInitialLegend();
            }

            // Apply correct font color to all nodes (including server
            // cluster summary nodes that arrive without a font color).
            if (typeof GraphExplorer.updateNodeFontColors === 'function') {
                var currentTheme = (typeof GraphExplorer.getTheme === 'function')
                    ? GraphExplorer.getTheme() : 'dark';
                GraphExplorer.updateNodeFontColors(currentTheme);
            }

            // Update legend window — defer so ExplorerWindows is guaranteed to be init'd
            setTimeout(function() {
                if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.updateLegend) {
                    ExplorerWindows.updateLegend();
                }
            }, 0);
            
            // Enable physics for layout (skip if server provided layout positions)
            const hasServerLayout = graphData.meta && graphData.meta.layout;

            // Will client-side clustering collapse 5000+ nodes into ~10 blobs?
            // If so, skip the expensive physics-on-5000-nodes entirely —
            // physics will be re-enabled after clustering completes.
            const willAutoCluster = typeof ExplorerClustering !== 'undefined' &&
                ExplorerClustering.autoClusterEnabled &&
                nodeCount >= (ExplorerClustering.clusterThreshold || 500);

            if (hasServerLayout) {
                // Server pre-computed positions — un-fix nodes so they can be
                // dragged freely.
                const allNodeData = nodes.get();
                const unfixUpdates = allNodeData
                    .filter(function(n) { return n.fixed; })
                    .map(function(n) { return { id: n.id, fixed: false }; });
                if (unfixUpdates.length > 0) {
                    nodes.update(unfixUpdates);
                }

                // Only auto-fit on first load. On subsequent refreshes the moveTo()
                // above already restores the user's viewport — fitting would snap
                // them away from where they deliberately navigated to.
                if (wasEmpty && network) {
                    setTimeout(function() {
                        try {
                            if (typeof GraphExplorer !== 'undefined' && GraphExplorer.syncNetworkSize) GraphExplorer.syncNetworkSize();
                            requestAnimationFrame(function() {
                                network.fit({ animation: { duration: 300 } });
                            });
                        } catch(e) {}
                    }, 100);
                }
                console.log('[ExplorerAPI] Server layout detected (' + graphData.meta.layout + ')');
            }

            if (!willAutoCluster && typeof GraphExplorer.enablePhysics === 'function') {
                GraphExplorer.enablePhysics();
            } else if (willAutoCluster) {
                console.log('[ExplorerAPI] Auto-cluster pending — physics deferred until after clustering');
            }
            
            // Log server-side clustering info if present
            var clusteringMeta = graphData.meta?.clustering;
            if (clusteringMeta && clusteringMeta.applied) {
                console.log('[ExplorerAPI] Server-side clustering:', {
                    totalNodes: clusteringMeta.total_nodes,
                    returnedNodes: clusteringMeta.returned_nodes,
                    clusterCount: clusteringMeta.cluster_count,
                    individualCount: clusteringMeta.individual_count,
                    autoSelected: clusteringMeta.auto_selected_centers,
                });
            }

            // Auto-cluster if graph is large (after physics setup)
            // This is the SECONDARY client-side pass using vis.js clustering
            // on top of the (already reduced) server-side payload.
            if (typeof ExplorerClustering !== 'undefined' && ExplorerClustering.autoClusterIfNeeded) {
                var clustered = ExplorerClustering.autoClusterIfNeeded(
                    nodeCount,
                    GraphExplorer.state?.currentColorAttribute || 'resource_name'
                );
                if (clustered) {
                    console.log('[ExplorerAPI] Client-side auto-clustering applied — stats:', ExplorerClustering.getClusterStats());

                    // Budget-based clustering keeps small types individual.
                    // If edges were stored outside vis.js (preClusterCheck),
                    // inject edges that connect currently-visible nodes so
                    // the individual nodes aren't floating disconnected.
                    if (preClusterCheck && apiState._storedEdges && apiState._storedEdges.length > 0) {
                        var visibleNodeIds = network.body.nodeIndices || [];
                        if (visibleNodeIds.length > 0) {
                            var visSet = {};
                            for (var vi = 0; vi < visibleNodeIds.length; vi++) visSet[visibleNodeIds[vi]] = true;
                            var toInject = [];
                            var existingEdgeIds = {};
                            var curEdges = edges.get();
                            for (var ce = 0; ce < curEdges.length; ce++) existingEdgeIds[curEdges[ce].id] = true;

                            for (var si = 0; si < apiState._storedEdges.length; si++) {
                                var se = apiState._storedEdges[si];
                                if (!existingEdgeIds[se.id] && visSet[se.from] && visSet[se.to]) {
                                    toInject.push(se);
                                }
                            }
                            if (toInject.length > 0) {
                                edges.add(toInject);
                                // Apply edge settings (color, label visibility)
                                if (typeof GraphExplorer.setEdgeColorByLabel === 'function') {
                                    GraphExplorer.setEdgeColorByLabel();
                                }
                                if (GraphExplorer.state && !GraphExplorer.state.edgeLabelsVisible) {
                                    var eLabelUpdates = [];
                                    toInject.forEach(function(e) {
                                        if (e.label !== undefined && e.label !== '') {
                                            eLabelUpdates.push({
                                                id: e.id,
                                                hiddenLabel: e.label,
                                                hiddenFontSize: (e.font && e.font.size) || 12,
                                                font: Object.assign({}, e.font, { size: 0 })
                                            });
                                        }
                                    });
                                    if (eLabelUpdates.length > 0) edges.update(eLabelUpdates);
                                }
                                console.log('[ExplorerAPI] Injected ' + toInject.length + ' edges for visible individual nodes');
                            }
                        }
                    }
                }
            }

            // If physics was deferred for auto-clustering, enable it now
            // that the expensive data-add + clustering is done and the
            // visible node count is small (~500 + a few cluster blobs).
            if (willAutoCluster && typeof GraphExplorer.enablePhysics === 'function') {
                GraphExplorer.enablePhysics();
                console.log('[ExplorerAPI] Physics enabled after auto-clustering');
            }

            console.log('[ExplorerAPI] Graph updated successfully:', {
                nodes: nodeCount,
                edges: edgeCount,
                aliases: graphData.aliases?.length || 0,
                edgesDeferred: deferEdges
            });

            // Store aggregation metadata (always null — diamond aggregation removed)
            // Client-side clustering (explorer-clustering.js) handles large graphs

            announceUpdate('Graph updated: ' + nodeCount + ' nodes, ' + edgeCount + ' edges' + (deferEdges ? ' (edges deferred)' : ''));
            return true;
        } catch (error) {
            console.error('[ExplorerAPI] Error updating graph:', error);
            announceUpdate('Error updating graph');
            return false;
        }
    }

    // ============================================
    // Differential Update (Alternative approach)
    // ============================================
    function updateGraphDifferential(graphData) {
        if (!graphData || graphData.error) {
            console.error('[ExplorerAPI] Cannot update graph:', graphData?.error || 'No data');
            return false;
        }

        const nodes = GraphExplorer.state.nodes;
        const edges = GraphExplorer.state.edges;

        if (!nodes || !edges) {
            console.error('[ExplorerAPI] Graph not initialized');
            return false;
        }

        try {
            const currentNodeIds = new Set(nodes.getIds());
            const currentEdgeIds = new Set(edges.getIds());
            const newNodeIds = new Set(graphData.nodes?.map(n => n.id) || []);
            const newEdgeIds = new Set(graphData.edges?.map(e => e.id) || []);

            // Remove nodes that no longer exist
            const nodesToRemove = [...currentNodeIds].filter(id => !newNodeIds.has(id));
            if (nodesToRemove.length > 0) {
                nodes.remove(nodesToRemove);
            }

            // Remove edges that no longer exist
            const edgesToRemove = [...currentEdgeIds].filter(id => !newEdgeIds.has(id));
            if (edgesToRemove.length > 0) {
                edges.remove(edgesToRemove);
            }

            // Update or add nodes
            const nodesToAdd = [];
            const nodesToUpdate = [];
            for (const node of (graphData.nodes || [])) {
                if (currentNodeIds.has(node.id)) {
                    nodesToUpdate.push(node);
                } else {
                    nodesToAdd.push(node);
                }
            }

            if (nodesToAdd.length > 0) nodes.add(nodesToAdd);
            if (nodesToUpdate.length > 0) nodes.update(nodesToUpdate);

            // Update or add edges
            const edgesToAdd = [];
            const edgesToUpdate = [];
            for (const edge of (graphData.edges || [])) {
                if (currentEdgeIds.has(edge.id)) {
                    edgesToUpdate.push(edge);
                } else {
                    edgesToAdd.push(edge);
                }
            }

            if (edgesToAdd.length > 0) edges.add(edgesToAdd);
            if (edgesToUpdate.length > 0) edges.update(edgesToUpdate);

            console.log('[ExplorerAPI] Differential update:', {
                nodesRemoved: nodesToRemove.length,
                nodesAdded: nodesToAdd.length,
                nodesUpdated: nodesToUpdate.length,
                edgesRemoved: edgesToRemove.length,
                edgesAdded: edgesToAdd.length,
                edgesUpdated: edgesToUpdate.length
            });

            return true;
        } catch (error) {
            console.error('[ExplorerAPI] Error in differential update:', error);
            return false;
        }
    }

    // ============================================
    // Refresh Graph (Main Entry Point)
    // ============================================
    async function refreshGraph(forceUpdate = false) {
        // Cancel any in-flight graph data fetch so this refresh takes
        // priority.  This prevents race conditions when the user rapidly
        // switches between checkpoints / bookmarks — only the LAST
        // refresh wins, and stale fetches are discarded.
        if (_graphFetchAbortController) {
            _graphFetchAbortController.abort();
        }
        _graphFetchAbortController = new AbortController();

        console.log('[ExplorerAPI] Refreshing graph...', forceUpdate ? '(forced)' : '');
        const graphData = await fetchGraphData(_graphFetchAbortController.signal);
        
        if (graphData && !graphData.error) {
            const success = updateGraph(graphData, forceUpdate);
            
            // Update empty state based on node count
            if (typeof GraphExplorer !== 'undefined' && GraphExplorer.checkEmptyState) {
                GraphExplorer.checkEmptyState();
            }

            // Paint the graph as soon as the primary payload arrives. Side
            // panels are lower priority and carry their own abort/generation
            // guard, so they can refresh without holding up graph interactivity.
            _refreshSideData().catch(function(err) {
                console.warn('[ExplorerAPI] Side data refresh failed:', err);
            });
            
            return success;
        }
        
        // Even on error/empty data, check empty state
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.checkEmptyState) {
            GraphExplorer.checkEmptyState();
        }
        
        return false;
    }

    /**
     * Refresh side-panel data (state + documents) in parallel.
     * Called after graph data loads; non-blocking.
     *
     * Cancels any in-flight request from a previous call so that stale
     * results never overwrite fresher data (fixes the race condition where
     * rapidfire USE BOOKMARK clicks caused interleaved state responses).
     */
    async function _refreshSideData() {
        // Cancel any in-flight side-data request
        if (_sideDataAbortController) {
            _sideDataAbortController.abort();
        }
        _sideDataAbortController = new AbortController();
        const signal = _sideDataAbortController.signal;
        const generation = ++_sideDataGeneration;

        try {
            const [stateData, docsData] = await Promise.all([
                fetchCursorState(signal),
                fetchGraphDocuments(signal)
            ]);

            // If a newer _refreshSideData() was launched while we were in
            // flight, discard these results — they are stale.
            if (generation !== _sideDataGeneration) {
                console.log('[ExplorerAPI] Discarding stale side-data (gen', generation, 'vs', _sideDataGeneration, ')');
                return;
            }

            // Dispatch state data to checkpoints/bookmarks panel
            if (stateData && typeof ExplorerWindows !== 'undefined') {
                if (ExplorerWindows.updateStateData) {
                    ExplorerWindows.updateStateData(stateData);
                }
            }

            // Distribute checkpoint/bookmark data to toolbar and tabs
            // so all UI stays in sync from a single /state fetch.
            if (stateData) {
                _distributeCheckpointBookmarkData(stateData);
            }

            // Dispatch documents data to documents panel
            if (docsData && typeof ExplorerWindows !== 'undefined') {
                if (ExplorerWindows.updateDocumentsData) {
                    ExplorerWindows.updateDocumentsData(docsData);
                }
            }
        } catch (err) {
            if (err.name === 'AbortError') {
                console.log('[ExplorerAPI] Side data refresh aborted (superseded by newer request)');
                return;
            }
            console.warn('[ExplorerAPI] Side data refresh failed:', err);
        }
    }

    /**
     * Parse checkpoint/bookmark arrays from /state response and push them
     * to ExplorerToolbar (sidebar lists) and ExplorerTabs (bottom bar).
     * This is the SINGLE distribution point — called from _refreshSideData
     * so that every /state fetch propagates everywhere.
     */
    function _distributeCheckpointBookmarkData(stateData) {
        // Parse checkpoints
        const rawCheckpoints = stateData.checkpoints || [];
        let checkpointsArray;
        if (Array.isArray(rawCheckpoints)) {
            checkpointsArray = rawCheckpoints.map(cp => ({ name: cp.name || cp }));
        } else if (typeof rawCheckpoints === 'object') {
            checkpointsArray = Object.keys(rawCheckpoints).map(name => ({ name }));
        } else {
            checkpointsArray = [];
        }

        // Parse bookmarks
        const rawBookmarks = stateData.bookmarks || [];
        let bookmarksArray;
        if (Array.isArray(rawBookmarks)) {
            bookmarksArray = rawBookmarks.map(bm => ({ name: bm.name || bm }));
        } else if (typeof rawBookmarks === 'object') {
            bookmarksArray = Object.keys(rawBookmarks).map(name => ({ name }));
        } else {
            bookmarksArray = [];
        }

        // Update toolbar sidebar lists
        if (typeof ExplorerToolbar !== 'undefined') {
            if (ExplorerToolbar.updateCheckpointsList) ExplorerToolbar.updateCheckpointsList(checkpointsArray);
            if (ExplorerToolbar.updateBookmarksList) ExplorerToolbar.updateBookmarksList(bookmarksArray);
        }

        // Update bottom sub-tabs bar
        if (typeof ExplorerTabs !== 'undefined') {
            if (ExplorerTabs.setCheckpoints) ExplorerTabs.setCheckpoints(checkpointsArray);
            if (ExplorerTabs.setBookmarks) ExplorerTabs.setBookmarks(bookmarksArray);
        }
    }

    async function refreshCursorPointers(stateData = null) {
        try {
            const state = stateData || await fetchCursorState();
            if (state) {
                _distributeCheckpointBookmarkData(state);
            }
            return state;
        } catch (err) {
            console.warn('[ExplorerAPI] Failed to refresh cursor pointers:', err);
            return null;
        }
    }

    // ============================================
    // Polling Control
    // ============================================
    function startPolling(intervalMs = null) {
        if (apiState.pollingInterval) {
            stopPolling();
        }

        const delay = intervalMs || apiState.pollingDelayMs;
        apiState.pollingEnabled = true;
        apiState.pollingInterval = setInterval(async () => {
            if (!apiState.isLoading) {
                await refreshGraph();
            }
        }, delay);

        console.log('[ExplorerAPI] Polling started:', delay, 'ms');
    }

    function stopPolling() {
        if (apiState.pollingInterval) {
            clearInterval(apiState.pollingInterval);
            apiState.pollingInterval = null;
        }
        apiState.pollingEnabled = false;
        console.log('[ExplorerAPI] Polling stopped');
    }

    function togglePolling() {
        if (apiState.pollingEnabled) {
            stopPolling();
        } else {
            startPolling();
        }
        return apiState.pollingEnabled;
    }

    // ============================================
    // Alias Management
    // ============================================
    function buildAliasUrl(aliasName) {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined || 
            !apiState.workspaceId || !apiState.cursorId) {
            console.warn('[ExplorerAPI] Missing required parameters for alias API call');
            return null;
        }

        // Build URL: {apiEndpoint}/workspaces/{workspaceId}/cursors/{cursorId}/alias/{aliasName}
        let url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/cursors/${apiState.cursorId}/alias/${encodeURIComponent(aliasName)}`;
        
        if (apiState.instanceId) {
            url += `?instance_id=${encodeURIComponent(apiState.instanceId)}`;
        }

        return url;
    }

    async function fetchAliasContent(aliasName) {
        if (!hasAuthentication()) {
            const embeddedAlias = getStandaloneAliasContent(aliasName);
            if (embeddedAlias) {
                console.log('[ExplorerAPI] Loaded embedded alias content:', aliasName, embeddedAlias);
                return embeddedAlias;
            }
        }
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot fetch alias');
            return { error: 'Authentication required', node_ids: [] };
        }
        
        const url = buildAliasUrl(aliasName);
        if (!url) {
            return { error: 'Missing API configuration', node_ids: [] };
        }

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders()
                }
            });

            if (!response.ok) {
                let errorDetail = response.statusText;
                try {
                    const errorBody = await response.json();
                    errorDetail = errorBody.detail || errorBody.message || errorDetail;
                } catch (e) {}
                throw new Error(`HTTP ${response.status}: ${errorDetail}`);
            }

            const data = await response.json();
            console.log('[ExplorerAPI] Fetched alias content:', aliasName, data);
            return data;
        } catch (error) {
            console.error('[ExplorerAPI] Failed to fetch alias content:', error);
            return { error: error.message, node_ids: [] };
        }
    }

    async function selectAlias(aliasName) {
        console.log('[ExplorerAPI] Selecting alias:', aliasName);
        const aliasData = await fetchAliasContent(aliasName);
        
        if (aliasData.error) {
            console.error('[ExplorerAPI] Cannot select alias:', aliasData.error);
            announceUpdate(`Failed to load alias: ${aliasName}`);
            return false;
        }

        const network = GraphExplorer.state.network;
        const nodes = GraphExplorer.state.nodes;
        
        if (!network || !nodes) {
            console.error('[ExplorerAPI] Graph not initialized');
            return false;
        }

        // Filter to only nodes that exist in the graph
        const nodeIds = aliasData.node_ids || [];
        const existingNodeIds = nodeIds.filter(id => nodes.get(id) !== null);
        
        console.log('[ExplorerAPI] Alias nodes:', nodeIds.length, 'total,', existingNodeIds.length, 'visible');

        if (existingNodeIds.length === 0) {
            announceUpdate(`Alias "${aliasName}" has no visible nodes`);
            return false;
        }

        // Select the nodes in the network
        network.setSelection({ nodes: existingNodeIds, edges: [] });
        
        // Update internal state to match
        GraphExplorer.state.selectedNodes = existingNodeIds;
        GraphExplorer.state.selectedAlias = aliasName;  // Track alias for SELECT operations
        
        // Notify about selection change (updates operator states)
        if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.updateOperatorStates) {
            ExplorerWindows.updateOperatorStates(existingNodeIds.length);
        }
        
        // Open the Selection window if not already open
        if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.open) {
            ExplorerWindows.open('selection');
        }
        
        // Update Selection window with selection
        updateSelectionWindow(existingNodeIds, aliasName);

        // Fit view to selected nodes — defer to next frame so vis.js
        // uses the correct canvas size when the chat panel is open.
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.syncNetworkSize) GraphExplorer.syncNetworkSize();
        requestAnimationFrame(function() {
            network.fit({
                nodes: existingNodeIds,
                animation: { duration: 300, easingFunction: 'easeInOutQuad' }
            });
        });

        announceUpdate(`Selected ${existingNodeIds.length} nodes from alias "${aliasName}"`);
        console.log('[ExplorerAPI] Selected alias:', aliasName, existingNodeIds.length, 'nodes');
        return true;
    }

    function updateSelectionWindow(nodeIds, aliasName) {
        // Update the Selection window with selected nodes
        // Open selection window if not already open
        if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.open) {
            ExplorerWindows.open('selection');
        }
        
        const detailsEmpty = document.getElementById('details-empty');
        const detailsContent = document.getElementById('details-content');
        const selectionSummary = document.getElementById('selection-summary');
        const selectedNodesList = document.getElementById('selected-nodes-list');
        
        if (detailsContent && selectedNodesList) {
            if (detailsEmpty) detailsEmpty.style.display = 'none';
            detailsContent.style.display = 'block';
            
            if (selectionSummary) {
                const aliasLabel = aliasName ? ` (${aliasName})` : '';
                safeSetInnerHTML(selectionSummary, `<span class="selection-count">${nodeIds.length} nodes selected${aliasLabel}</span>`);
            }
            
            // Build node list
            const nodes = GraphExplorer.state.nodes;
            safeSetInnerHTML(selectedNodesList, nodeIds.map(id => {
                const node = nodes.get(id);
                if (!node) return '';
                return `
                    <div class="selected-node-item" data-node-id="${id}">
                        <span class="node-color" style="background: ${node.color?.background || node.color || '#666'}"></span>
                        <span class="node-label">${node.label || id}</span>
                        <button class="node-focus-btn" onclick="GraphExplorer.focusNode('${id}', {scale: 1.5, animation: true})" title="Focus">🎯</button>
                    </div>
                `;
            }).join(''));
        }
    }

    function updateAliasesList(aliases) {
        // Update the Aliases window with the list of aliases
        const aliasesList = document.getElementById('aliases-list');
        if (!aliasesList) {
            console.log('[ExplorerAPI] aliases-list element not found, will update when window opens');
            return;
        }
        
        console.log('[ExplorerAPI] Updating aliases list:', aliases?.length || 0, 'aliases');
        
        if (!aliases || aliases.length === 0) {
            safeSetInnerHTML(aliasesList, '<div class="window-empty">No aliases defined</div>');
            return;
        }

        safeSetInnerHTML(aliasesList, aliases.map(alias => `
            <div class="alias-item" data-alias="${alias}">
                <span class="alias-icon"><i data-lucide="bookmark"></i></span>
                <span class="alias-name">${alias}</span>
                <button class="alias-select-btn" data-select-alias="${alias}" title="Select nodes">
                    <i data-lucide="mouse-pointer-click"></i>
                    <span>Select</span>
                </button>
            </div>
        `).join(''));
        
        // Re-initialize Lucide icons for new elements
        if (typeof lucide !== 'undefined') {
            lucide.createIcons();
        }
        
        // Use event delegation for alias clicks
        aliasesList.onclick = function(e) {
            const btn = e.target.closest('[data-select-alias]');
            if (btn) {
                const aliasName = btn.getAttribute('data-select-alias');
                console.log('[ExplorerAPI] Alias button clicked:', aliasName);
                selectAlias(aliasName);
            }
        };
    }
    
    function getAliases() {
        return apiState.aliases || [];
    }

    // ============================================
    // MQL Query Execution
    // ============================================
    function buildExecuteUrl() {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined || 
            !apiState.workspaceId) {
            console.warn('[ExplorerAPI] Missing required parameters for execute API call');
            return null;
        }

        // Build URL: {apiEndpoint}/workspaces/{workspaceId}/query
        return `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/query`;
    }

    async function executeMQL(query) {
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot execute MQL');
            showConfigDialog();
            throw new Error('Authentication required');
        }
        
        const url = buildExecuteUrl();
        if (!url) {
            throw new Error('Missing API configuration');
        }

        // Any MQL execution can change cursor state (FIND, EXTEND, CONNECT,
        // USE PATTERN, RESTORE, USE BOOKMARK, SELECT, EXCLUDE, etc.).
        // Always reset the data hash so the subsequent refreshGraph applies
        // the update even if the node/edge IDs happen to match the
        // currently-displayed graph.
        apiState.lastDataHash = null;
        if (_graphFetchAbortController) {
            _graphFetchAbortController.abort();
            _graphFetchAbortController = null;
        }

        console.log('[ExplorerAPI] Executing MQL:', query);
        showLoadingIndicator(true);

        try {
            const fetchOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({
                    query: query,
                    instance_id: apiState.instanceId || undefined,
                    cursor_id: apiState.cursorId || 'me'
                })
            };
            if (shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }
            const response = await fetch(url, fetchOptions);

            if (!response.ok) {
                let errorDetail = response.statusText;
                try {
                    const errorBody = await response.json();
                    errorDetail = errorBody.detail || errorBody.message || errorDetail;
                } catch (e) {}
                throw new Error(`HTTP ${response.status}: ${errorDetail}`);
            }

            const data = await response.json();
            console.log('[ExplorerAPI] MQL execution result:', data);

            // Pin the resolved cursor returned by the API.  Even when the
            // request used the "me" alias, subsequent graph/data, state, and
            // documents calls must target the exact cursor instance that just
            // executed the FQL.  Otherwise notebook embeds can refresh a fresh
            // or different "me" cursor and render an empty graph.
            if (data && data.cursor_id && apiState.cursorId !== data.cursor_id) {
                setCursorId(data.cursor_id, true);
            }
            
            // Check for warnings in the response
            if (data && data.warning) {
                console.warn('[ExplorerAPI] MQL warning:', data.warning);
                showWarningNotification(data.warning);
            }
            
            return data;
        } catch (error) {
            console.error('[ExplorerAPI] MQL execution failed:', error);
            throw error;
        } finally {
            showLoadingIndicator(false);
        }
    }

    // ============================================
    // Cursor Utilities (Save / Reset)
    // ============================================
    function buildCursorStateUrl(cursorId) {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined || 
            !apiState.workspaceId || !cursorId) {
            console.warn('[ExplorerAPI] Missing parameters for cursor state');
            return null;
        }

        let url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/cursors/${cursorId}/state`;
        if (apiState.instanceId) {
            url += `?instance_id=${encodeURIComponent(apiState.instanceId)}`;
        }
        return url;
    }

    function buildCreateCursorUrl() {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined || 
            !apiState.workspaceId) {
            console.warn('[ExplorerAPI] Missing parameters for cursor create');
            return null;
        }

        let url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/cursors/`;
        if (apiState.instanceId) {
            url += `?instance_id=${encodeURIComponent(apiState.instanceId)}`;
        }
        return url;
    }

    function buildResetCursorUrl(cursorId) {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined || 
            !apiState.workspaceId || !cursorId) {
            console.warn('[ExplorerAPI] Missing parameters for cursor reset');
            return null;
        }

        let url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/cursors/${cursorId}/reset`;
        if (apiState.instanceId) {
            url += `?instance_id=${encodeURIComponent(apiState.instanceId)}`;
        }
        return url;
    }

    function buildRefreshCursorUrl(cursorId) {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined ||
            !apiState.workspaceId || !cursorId) {
            console.warn('[ExplorerAPI] Missing parameters for cursor refresh');
            return null;
        }

        let url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/cursors/${cursorId}/refresh`;
        if (apiState.instanceId) {
            url += `?instance_id=${encodeURIComponent(apiState.instanceId)}`;
        }
        return url;
    }

    async function refreshMeCursor() {
        /**
         * Refresh the 'me' cursor by replaying its FQL history on the
         * current (potentially rebuilt) instance.  This preserves the
         * user's exploration context across graph rebuilds.
         *
         * Falls back to resetMeCursor() if the refresh endpoint is
         * unavailable (e.g. older API version).
         */
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot refresh cursor');
            showConfigDialog();
            throw new Error('Authentication required');
        }

        const refreshUrl = buildRefreshCursorUrl('me');
        if (!refreshUrl) {
            throw new Error('Missing API configuration');
        }

        const fetchOptions = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders()
            }
        };
        if (shouldIncludeCredentials()) fetchOptions.credentials = 'include';

        const response = await fetch(refreshUrl, fetchOptions);

        if (!response.ok) {
            let errorDetail = response.statusText;
            try {
                const errorBody = await response.json();
                errorDetail = errorBody.detail || errorBody.message || errorDetail;
            } catch (e) {}
            throw new Error(`HTTP ${response.status}: ${errorDetail}`);
        }

        const data = await response.json();
        // Do NOT reset cursorId to 'me' — the refresh preserves the existing cursor
        return data;
    }

    async function saveMeCursor(name = null) {
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot save cursor');
            showConfigDialog();
            throw new Error('Authentication required');
        }

        const cursorName = (name || prompt('Save cursor as', `Saved Cursor ${new Date().toLocaleString()}`) || '').trim();
        if (!cursorName) {
            return null;
        }

        const stateUrl = buildCursorStateUrl(apiState.cursorId || 'me');
        const createUrl = buildCreateCursorUrl();
        if (!stateUrl || !createUrl) {
            throw new Error('Missing API configuration');
        }

        const stateResponse = await fetch(stateUrl, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders()
            }
        });

        if (!stateResponse.ok) {
            let errorDetail = stateResponse.statusText;
            try {
                const errorBody = await stateResponse.json();
                errorDetail = errorBody.detail || errorBody.message || errorDetail;
            } catch (e) {}
            throw new Error(`HTTP ${stateResponse.status}: ${errorDetail}`);
        }

        const state = await stateResponse.json();

        const createResponse = await fetch(createUrl, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({
                name: cursorName,
                state: state || {}
            })
        });

        if (!createResponse.ok) {
            let errorDetail = createResponse.statusText;
            try {
                const errorBody = await createResponse.json();
                errorDetail = errorBody.detail || errorBody.message || errorDetail;
            } catch (e) {}
            throw new Error(`HTTP ${createResponse.status}: ${errorDetail}`);
        }

        const savedCursor = await createResponse.json();
        if (savedCursor && savedCursor.id) {
            setCursorId(savedCursor.id, true);
        }
        return savedCursor;
    }

    async function createNewCursor(name = null) {
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot create cursor');
            showConfigDialog();
            throw new Error('Authentication required');
        }

        const createUrl = buildCreateCursorUrl();
        if (!createUrl) {
            throw new Error('Missing API configuration');
        }

        const cursorName = name || `search-${Date.now()}`;
        const fetchOptions = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...getAuthHeaders()
            },
            body: JSON.stringify({ name: cursorName })
        };

        if (shouldIncludeCredentials()) {
            fetchOptions.credentials = 'include';
        }

        const response = await fetch(createUrl, fetchOptions);

        if (!response.ok) {
            let errorDetail = response.statusText;
            try {
                const errorBody = await response.json();
                errorDetail = errorBody.detail || errorBody.message || errorDetail;
            } catch (e) {}
            throw new Error(`HTTP ${response.status}: ${errorDetail}`);
        }

        const newCursor = await response.json();
        console.log('[ExplorerAPI] Created new cursor:', newCursor.id);
        return newCursor;
    }

    async function resetMeCursor() {
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot reset cursor');
            showConfigDialog();
            throw new Error('Authentication required');
        }

        const resetUrl = buildResetCursorUrl('me');
        if (!resetUrl) {
            throw new Error('Missing API configuration');
        }

        const response = await fetch(resetUrl, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders()
            }
        });

        if (!response.ok) {
            let errorDetail = response.statusText;
            try {
                const errorBody = await response.json();
                errorDetail = errorBody.detail || errorBody.message || errorDetail;
            } catch (e) {}
            throw new Error(`HTTP ${response.status}: ${errorDetail}`);
        }

        const data = await response.json();
        setCursorId('me', true);
        
        // Clear aliases cache and stale data hash on cursor reset
        apiState.aliases = [];
        apiState.lastDataHash = null;
        clearPointerGraphCache('me');
        
        return data;
    }

    async function clearGraph() {
        /**
         * Clear the current graph selection by executing CLEAR; via FQL.
         * Preserves checkpoints, bookmarks, and history.
         * Also clears vis.js nodes/edges and shows empty state.
         */
        console.log('[ExplorerAPI] Clearing graph selection (preserving checkpoints/bookmarks)');
        const result = await executeMQL('CLEAR;');

        // Invalidate data hash so next fetch does a full refresh
        apiState.lastDataHash = null;
        // Clear cached aliases – they were dropped on the backend
        apiState.aliases = [];

        // Clear vis.js nodes/edges and show empty state
        if (typeof GraphExplorer !== 'undefined' && GraphExplorer.state) {
            if (GraphExplorer.state.nodes) GraphExplorer.state.nodes.clear();
            if (GraphExplorer.state.edges) GraphExplorer.state.edges.clear();
            if (GraphExplorer.showEmptyState) GraphExplorer.showEmptyState();
        }
        // Update aliases list to empty
        updateAliasesList([]);

        return result;
    }
    
    // ============================================
    // Warning Notification
    // ============================================
    function showWarningNotification(message) {
        // Remove existing warning
        const existing = document.querySelector('.api-warning-notification');
        if (existing) existing.remove();
        
        const notification = document.createElement('div');
        notification.className = 'api-warning-notification';
        safeSetInnerHTML(notification, `
            <span class="warning-icon">⚠️</span>
            <span class="warning-message">${escapeHtml(message)}</span>
            <button class="warning-close" onclick="this.parentElement.remove()">×</button>
        `);
        document.body.appendChild(notification);
        
        // Auto-remove after 6 seconds
        setTimeout(() => {
            if (notification.parentElement) {
                notification.remove();
            }
        }, 6000);
    }
    
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    // ============================================
    // Loading Indicator
    // ============================================
    /**
     * Sequential loading messages.
     * @type {string[]}
     */
    const LOADING_STEPS = [
        'Accessing the instance ...',
        'Operating on cursor ...',
        'Fetching cursor state ...',
        'Polishing results ...',
    ];
    const REBUILD_LOADING_STEPS = [
        'Rebuilding knowledge graph ...',
        'Processing data sources ...',
        'Reconstructing relationships ...',
        'Replaying cursor history ...',
        'Finalizing graph ...',
    ];
    let _loadingStepTimer = null;
    let _loadingStepIndex = 0;
    let _activeLoadingSteps = LOADING_STEPS;

    function _advanceLoadingStep() {
        const loadingText = document.getElementById('loadingBar-text');
        const dots = document.querySelectorAll('#loadingBar .loading-step-dot');
        if (_loadingStepIndex < _activeLoadingSteps.length - 1) {
            _loadingStepIndex++;
        }
        if (loadingText) loadingText.textContent = _activeLoadingSteps[_loadingStepIndex];
        dots.forEach((dot, i) => {
            dot.classList.toggle('done', i < _loadingStepIndex);
            dot.classList.toggle('active', i === _loadingStepIndex);
        });
    }

    /**
     * Show or hide the loading overlay.
     * @param {boolean} show   - true to show, false to hide
     * @param {string}  [mode] - 'cursor' (default) or 'rebuild'
     */
    function showLoadingIndicator(show, mode) {
        const loadingBar = document.getElementById('loadingBar');
        const loadingText = document.getElementById('loadingBar-text');
        const dotsContainer = loadingBar?.querySelector('.loading-step-dots');
        const dots = loadingBar?.querySelectorAll('.loading-step-dot') || [];
        const progress = document.getElementById('loadingBar-progress');
        
        if (loadingBar) {
            if (show) {
                // Choose the right step messages for the mode
                const isRebuild = mode === 'rebuild';
                _activeLoadingSteps = isRebuild ? REBUILD_LOADING_STEPS : LOADING_STEPS;

                loadingBar.classList.remove('hidden');
                loadingBar.style.display = 'flex';
                // Toggle a data-mode attribute so CSS can style rebuild differently
                loadingBar.setAttribute('data-mode', isRebuild ? 'rebuild' : 'cursor');
                if (progress) progress.classList.remove('visible');

                // Start sequential message cycling
                _loadingStepIndex = 0;
                if (loadingText) loadingText.textContent = _activeLoadingSteps[0];
                if (dotsContainer) dotsContainer.style.display = 'flex';

                // Rebuild has 5 steps; cursor has 4 — sync visible dots
                dots.forEach((d, i) => {
                    const inRange = i < _activeLoadingSteps.length;
                    d.style.display = inRange ? '' : 'none';
                    d.classList.toggle('active', i === 0 && inRange);
                    d.classList.remove('done');
                });

                if (_loadingStepTimer) clearInterval(_loadingStepTimer);
                // Rebuild is slower — advance every 3 s; cursor every 1.8 s
                const interval = isRebuild ? 3000 : 1800;
                _loadingStepTimer = setInterval(_advanceLoadingStep, interval);
            } else {
                if (_loadingStepTimer) {
                    clearInterval(_loadingStepTimer);
                    _loadingStepTimer = null;
                }
                loadingBar.removeAttribute('data-mode');
                loadingBar.classList.add('hidden');
            }
        }
    }

    // ============================================
    // Accessibility Announcer
    // ============================================
    function announceUpdate(message) {
        const announcer = document.getElementById('sr-announcer');
        if (announcer) {
            announcer.textContent = message;
        }
    }

    // ============================================
    // Configuration Updates
    // ============================================
    function setConfig(config) {
        if (config.apiEndpoint !== undefined) apiState.apiEndpoint = config.apiEndpoint;
        if (config.workspaceId !== undefined) {
            apiState.workspaceId = config.workspaceId;
            // Clear stale data hash — new workspace means new graph data
            apiState.lastDataHash = null;
        }
        if (config.cursorId !== undefined) {
            const previousCursorId = apiState.cursorId;
            setCursorId(config.cursorId, true);
            // Clear stale data hash — new cursor means new graph data
            apiState.lastDataHash = null;
            apiState.aliases = [];
            if (config.clearPointerCache !== false) {
                clearPointerGraphCache(config.cursorId);
                if (previousCursorId && previousCursorId !== config.cursorId) {
                    clearPointerGraphCache(previousCursorId);
                }
            } else if (previousCursorId && previousCursorId !== config.cursorId) {
                clearPointerGraphCache(previousCursorId);
            }
        }
        if (config.instanceId !== undefined) apiState.instanceId = config.instanceId;
        if (config.performanceMode !== undefined) apiState.performanceMode = config.performanceMode === 'large' ? 'large' : 'auto';
        
        updateApiKeyButtonVisibility();
        console.log('[ExplorerAPI] Config updated:', apiState);
    }

    function getConfig() {
        return { ...apiState };
    }

    // ============================================
    // Cursor History
    // ============================================
    function buildCursorHistoryUrl(cursorId) {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined || 
            !apiState.workspaceId || !cursorId) {
            console.warn('[ExplorerAPI] Missing parameters for cursor history');
            return null;
        }

        let url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/cursors/${cursorId}/history`;
        if (apiState.instanceId) {
            url += `?instance_id=${encodeURIComponent(apiState.instanceId)}`;
        }
        return url;
    }

    async function fetchCursorHistory() {
        if (!hasAuthentication()) {
            const embeddedHistory = getStandaloneCursorHistory();
            if (embeddedHistory) {
                console.log('[ExplorerAPI] Loaded embedded cursor history:', {
                    statements: embeddedHistory.statements?.length || 0,
                    params: Object.keys(embeddedHistory.params || {}).length
                });
                return embeddedHistory;
            }
        }
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot fetch cursor history');
            return { statements: [], params: {}, statements_count: 0 };
        }

        const cursorId = apiState.cursorId || 'me';
        const url = buildCursorHistoryUrl(cursorId);
        if (!url) {
            return { statements: [], params: {}, statements_count: 0 };
        }

        try {
            const fetchOptions = {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    ...getAuthHeaders()
                }
            };

            if (shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }

            const response = await fetch(url, fetchOptions);

            if (!response.ok) {
                let errorDetail = response.statusText;
                try {
                    const errorBody = await response.json();
                    errorDetail = errorBody.detail || errorBody.message || errorDetail;
                } catch (e) {}
                throw new Error(`HTTP ${response.status}: ${errorDetail}`);
            }

            const data = await response.json();
            console.log('[ExplorerAPI] Fetched cursor history:', {
                statements: data.statements?.length || 0,
                params: Object.keys(data.params || {}).length
            });

            return data;
        } catch (error) {
            console.error('[ExplorerAPI] Failed to fetch cursor history:', error);
            return { statements: [], params: {}, statements_count: 0 };
        }
    }

    // ============================================
    // Search via A2A Tasks - Text Analysis + Cursor Loading
    // ============================================
    function buildSearchTaskUrl() {
        // Empty string is valid for apiEndpoint (dev mode with Vite proxy)
        if (apiState.apiEndpoint === null || apiState.apiEndpoint === undefined || 
            !apiState.workspaceId) {
            console.warn('[ExplorerAPI] Missing parameters for search task');
            return null;
        }

        return `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/a2a/tasks`;
    }

    /**
     * Poll a task until it reaches a terminal state (completed, failed, cancelled).
     * Returns the final task data.  Used by searchAsk to keep the loading wheel
     * visible while the agent processes the initial query.
     *
     * @param {string} taskId      - A2A task ID
     * @param {number} intervalMs  - Polling interval (default 2000ms)
     * @param {number} maxWaitMs   - Maximum wait time (default 120000ms = 2 min)
     * @returns {Object} Final task data
     */
    async function _pollTaskUntilComplete(taskId, intervalMs = 2000, maxWaitMs = 120000) {
        const url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/a2a/tasks/${taskId}`;
        const headers = {
            'Accept': 'application/json',
            ...getAuthHeaders()
        };
        const fetchOpts = { method: 'GET', headers };
        if (shouldIncludeCredentials()) fetchOpts.credentials = 'include';

        const TERMINAL = new Set(['completed', 'failed', 'cancelled']);
        const deadline = Date.now() + maxWaitMs;

        while (Date.now() < deadline) {
            await new Promise(r => setTimeout(r, intervalMs));
            try {
                const res = await fetch(url, fetchOpts);
                if (!res.ok) {
                    console.warn('[ExplorerAPI] Poll task HTTP', res.status);
                    continue;
                }
                const task = await res.json();
                const state = task?.status || task?.state || '';
                console.log('[ExplorerAPI] Poll task', taskId.substring(0, 8), 'status:', state);
                if (TERMINAL.has(state)) return task;
            } catch (err) {
                console.warn('[ExplorerAPI] Poll task error:', err);
            }
        }

        console.warn('[ExplorerAPI] Poll task timed out after', maxWaitMs, 'ms');
        return null;
    }

    async function _streamTaskUntilComplete(taskId, maxWaitMs = 120000) {
        const url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/a2a/tasks/${taskId}/stream`;
        const headers = {
            'Accept': 'text/event-stream',
            ...getAuthHeaders()
        };
        const fetchOpts = { method: 'GET', headers };
        if (shouldIncludeCredentials()) fetchOpts.credentials = 'include';

        const controller = new AbortController();
        fetchOpts.signal = controller.signal;
        const timeout = setTimeout(() => controller.abort(), maxWaitMs);
        const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

        try {
            const res = await fetch(url, fetchOpts);
            if (!res.ok || !res.body) {
                throw new Error(`Stream unavailable: HTTP ${res.status}`);
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const events = buffer.split('\n\n');
                buffer = events.pop() || '';
                for (const eventText of events) {
                    const dataLines = eventText
                        .split('\n')
                        .filter(line => line.startsWith('data:'))
                        .map(line => line.slice(5).trim());
                    if (!dataLines.length) continue;
                    let eventData = null;
                    try {
                        eventData = JSON.parse(dataLines.join('\n'));
                    } catch (_) {
                        continue;
                    }
                    const state = eventData.status || eventData.state || '';
                    if (TERMINAL.has(state)) {
                        return { status: state };
                    }
                }
            }
        } finally {
            clearTimeout(timeout);
        }
        return null;
    }

    async function _fetchTask(taskId) {
        const url = `${apiState.apiEndpoint}/workspaces/${apiState.workspaceId}/a2a/tasks/${taskId}`;
        const fetchOpts = {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                ...getAuthHeaders()
            }
        };
        if (shouldIncludeCredentials()) fetchOpts.credentials = 'include';
        const res = await fetch(url, fetchOpts);
        if (!res.ok) return null;
        return res.json();
    }

    async function _waitTaskUntilComplete(taskId) {
        try {
            const streamed = await _streamTaskUntilComplete(taskId);
            if (streamed) return await _fetchTask(taskId) || streamed;
        } catch (err) {
            console.warn('[ExplorerAPI] Task stream unavailable, falling back to polling:', err);
        }
        return _pollTaskUntilComplete(taskId);
    }

    /**
     * Execute search via A2A task - analyzes text to find entities and loads them into a cursor.
     * Creates a task with agent_id parameter (default "sibyl").
     * After completion, refreshes the graph to show the results.
     * 
     * @param {string} text - The search query text
     * @param {Array} keywords - Pre-identified entities from autocomplete [{id, label, type}]
     * @param {Object} options - Optional settings
     * @param {string} options.cursorId - Cursor ID to load results into (default: 'me')
     * @param {string} options.agent_id - Agent ID to publish the task to (default: 'sibyl')
     * @param {boolean} options.resetCursor - Reset cursor before loading (default: false)
     * @param {boolean} options.createCursor - Create a new cursor for this search (default: false).
     *   When true, a fresh cursor is created via POST /cursors/ and the search task
     *   is executed against that cursor. This allows multiple concurrent searches
     *   each with their own isolated cursor state.
     * @param {boolean} options.openTaskCard - Open task card window after search (default: true)
     * @param {boolean} options.waitForCompletion - Poll until the task reaches a terminal
     *   state before refreshing the graph and hiding the loading wheel.  Intended for the
     *   initial search where graph data is only available after the agent has processed
     *   the query.  For subsequent queries (sent from within the chat), set this to false
     *   so the explorer stays interactive while the agent works. (default: false)
     * @returns {Object} Task result with search_result containing matched entities and cursor state
     */
    async function searchAsk(text, keywords = [], options = {}) {
        if (!hasAuthentication()) {
            console.warn('[ExplorerAPI] No authentication available - cannot execute search task');
            showConfigDialog();
            throw new Error('Authentication required');
        }
        
        const url = buildSearchTaskUrl();
        if (!url) {
            throw new Error('Missing API configuration');
        }

        let cursorId = options.cursorId || apiState.cursorId || 'me';
        const resetCursor = options.resetCursor || false;
        const shouldCreateCursor = options.createCursor || false;
        const openTaskCard = options.openTaskCard !== false;  // Default to true
        const waitForCompletion = options.waitForCompletion || false;

        // If createCursor is requested, create a new isolated cursor first
        if (shouldCreateCursor) {
            try {
                const cursorName = text ? text.substring(0, 100) : null;
                const newCursor = await createNewCursor(cursorName);
                cursorId = newCursor.id;
                // Switch the explorer to use the new cursor
                setCursorId(cursorId, true);
                console.log('[ExplorerAPI] Using newly created cursor for search:', cursorId);
            } catch (err) {
                console.error('[ExplorerAPI] Failed to create new cursor, falling back to:', cursorId, err);
            }
        }

        console.log('[ExplorerAPI] Executing search via a2a/tasks:', { text, keywords, cursorId, resetCursor, createCursor: shouldCreateCursor, waitForCompletion });
        showLoadingIndicator(true);

        try {
            // Map UI types to API schema entity_type values
            // UI returns: concept, edge_type, node, etc.
            // API expects: node, edge, taxonomy_resource, ontology_type, taxonomy_type
            const typeMapping = {
                'concept': 'taxonomy_resource',
                'edge_type': 'ontology_type',
                'node': 'node',
                'edge': 'ontology_type',
                'taxonomy_resource': 'taxonomy_resource',
                'ontology_type': 'ontology_type',
                'taxonomy_type': 'taxonomy_resource',
            };
            
            // Transform keywords to identified_concepts format expected by API
            const identifiedConcepts = keywords.map(k => ({
                id: k.id,
                name: k.label || k.name,
                entity_type: typeMapping[k.type] || 'node',
                mixin_name: k.mixin_name || null
            }));

            // Build A2A task request with search_text parameter
            const requestBody = {
                agent_id: options.agent_id || 'sibyl',  // Use provided agent or default to sibyl
                search_text: text || '',
                identified_concepts: identifiedConcepts,
                cursor_id: cursorId,
                reset_cursor: resetCursor,
            };

            const fetchOptions = {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify(requestBody)
            };

            // Include credentials for cookie-based authentication
            if (shouldIncludeCredentials()) {
                fetchOptions.credentials = 'include';
            }

            const response = await fetch(url, fetchOptions);

            if (!response.ok) {
                let errorDetail = response.statusText;
                try {
                    const errorBody = await response.json();
                    // Handle FastAPI validation errors (array of objects)
                    if (Array.isArray(errorBody.detail)) {
                        errorDetail = errorBody.detail.map(e => 
                            e.msg || e.message || JSON.stringify(e)
                        ).join('; ');
                    } else {
                        errorDetail = errorBody.detail || errorBody.message || errorDetail;
                    }
                } catch (e) {}
                throw new Error(`HTTP ${response.status}: ${errorDetail}`);
            }

            const taskData = await response.json();
            const searchResult = taskData.search_result || {};
            
            console.log('[ExplorerAPI] Search task result:', {
                taskId: taskData.id,
                matchedEntities: searchResult.matched_entities?.length || 0,
                identifiedConceptsUsed: searchResult.identified_concepts_used?.length || 0,
                generatedQueries: searchResult.generated_queries?.length || 0,
                totalEntitiesLoaded: searchResult.total_entities_loaded || 0
            });

            // If the cursor was reset, clear the chat task tabs so stale tasks
            // from the previous cursor are removed (mirrors ExplorerCursor.resetCursorView behaviour).
            if (resetCursor && typeof ExplorerChat !== 'undefined' && ExplorerChat.resetChat) {
                ExplorerChat.resetChat();
            }

            // Open chat panel with this task (do this before polling so the
            // chat can start streaming the response in parallel)
            if (openTaskCard && taskData.id && typeof ExplorerChat !== 'undefined' && ExplorerChat.openChatWithTask) {
                ExplorerChat.openChatWithTask(taskData.id, text);
            }

            // If waitForCompletion is requested (initial search), keep the
            // loading wheel and poll the task until a terminal state, THEN
            // refresh the graph with the fully-loaded data.
            if (waitForCompletion && taskData.id) {
                const taskStatus = taskData.status || taskData.state || '';
                const isTerminal = ['completed', 'failed', 'cancelled'].includes(taskStatus);

                if (!isTerminal) {
                    console.log('[ExplorerAPI] Waiting for task to complete before refreshing graph...');
                    const finalTask = await _waitTaskUntilComplete(taskData.id);
                    const finalResult = finalTask?.search_result || {};
                    const resolvedCursorId = await _finalizeSearchTask(
                        finalTask || taskData,
                        cursorId,
                        { navigateBookmarks: options.navigateBookmarks },
                    );
                    cursorId = resolvedCursorId;
                    console.log('[ExplorerAPI] Task complete, graph refreshed for cursor:', cursorId);

                    // Tell the chat to reload its task list so the status badge
                    // updates from "in_progress" → "completed", and also reload
                    // the active task's content in case the initial load failed
                    // (e.g. due to transient 502/CORS errors).
                    if (typeof ExplorerChat !== 'undefined' && ExplorerChat.isOpen && ExplorerChat.isOpen()) {
                        try {
                            const chatInst = ExplorerChat._getChatInstance?.();
                            if (chatInst) {
                                if (chatInst._loadTasks) chatInst._loadTasks();
                                // Reload the active task tab content if it matches
                                // the task that just completed.
                                if (chatInst.currentTaskId === taskData.id && chatInst._loadTaskHistory) {
                                    chatInst._loadTaskHistory();
                                }
                            }
                        } catch (_) { /* ignore */ }
                    }

                    return {
                        task_id: taskData.id,
                        original_text: finalResult.original_text || searchResult.original_text || text,
                        matched_entities: finalResult.matched_entities || searchResult.matched_entities || [],
                        identified_concepts_used: finalResult.identified_concepts_used || searchResult.identified_concepts_used || [],
                        generated_queries: finalResult.generated_queries || searchResult.generated_queries || [],
                        cursor_id: cursorId,
                        cursor_result: finalResult.cursor_result || searchResult.cursor_result || null,
                        total_entities_loaded: finalResult.total_entities_loaded || searchResult.total_entities_loaded || 0,
                        task: finalTask || taskData,
                    };
                }
            }

            cursorId = await _finalizeSearchTask(taskData, cursorId, {
                navigateBookmarks: options.navigateBookmarks,
            });

            // Return data in a format compatible with the old search/ask response
            return {
                task_id: taskData.id,
                original_text: searchResult.original_text || text,
                matched_entities: searchResult.matched_entities || [],
                identified_concepts_used: searchResult.identified_concepts_used || [],
                generated_queries: searchResult.generated_queries || [],
                cursor_id: cursorId,
                cursor_result: searchResult.cursor_result || null,
                total_entities_loaded: searchResult.total_entities_loaded || 0,
                // Include full task data for task card
                task: taskData
            };
        } catch (error) {
            console.error('[ExplorerAPI] Search task failed:', error);
            throw error;
        } finally {
            showLoadingIndicator(false);
        }
    }

    // ============================================
    // Export API
    // ============================================
    global.ExplorerAPI = {
        init: init,
        fetchGraphData: fetchGraphData,
        updateGraph: updateGraph,
        updateGraphDifferential: updateGraphDifferential,
        refreshGraph: refreshGraph,
        rebuildGraph: rebuildGraph,
        // Polling removed - no longer needed
        startPolling: function() { console.log('[ExplorerAPI] Polling disabled'); },
        stopPolling: function() { console.log('[ExplorerAPI] Polling disabled'); },
        togglePolling: function() { console.log('[ExplorerAPI] Polling disabled'); return false; },
        setConfig: setConfig,
        getConfig: getConfig,
        // Auth methods - API key dialog removed, uses bearer token only
        setApiKey: setApiKey,
        clearApiKey: clearApiKey,
        promptForApiKey: function() { console.log('[ExplorerAPI] API key prompt disabled - uses bearer auth'); return false; },
        showConfigDialog: function() { console.log('[ExplorerAPI] Config dialog disabled'); },
        hasAuthentication: hasAuthentication,
        isConfigured: isConfigured,
        isReady: isReady,
        loadAuthFromSessionStorage: loadAuthFromSessionStorage,
        // Auth helpers for other modules
        getAuthHeaders: getAuthHeaders,
        shouldIncludeCredentials: shouldIncludeCredentials,
        // Alias methods
        fetchAliasContent: fetchAliasContent,
        selectAlias: selectAlias,
        updateAliasesList: updateAliasesList,
        getAliases: getAliases,
        // Legend filters
        setNodeTypeFilter: setNodeTypeFilter,
        setEdgeNameFilter: setEdgeNameFilter,
        setEntrypointTaxonomyId: setEntrypointTaxonomyId,
        setTrimFilter: setTrimFilter,
        getActiveFilters: getActiveFilters,
        clearAllFilters: clearAllFilters,
        // Aggregation & extended edges
        setExtendedEdges: setExtendedEdges,
        setMaxNodes: setMaxNodes,
        setPerformanceMode: setPerformanceMode,
        togglePerformanceMode: togglePerformanceMode,
        getPerformanceMode: function() { return apiState.performanceMode; },
        isInteractionDeferred: isInteractionDeferred,
        getMaxNodes: function() { return apiState.maxNodes; },
        isAggregateNode: isAggregateNode,
        explodeAggregate: explodeAggregate,
        getAggregationMeta: getAggregationMeta,
        // Stored edges (kept outside vis.js DataSet for performance during clustering)
        getStoredEdges: function() { return apiState._storedEdges; },
        clearStoredEdges: function() { apiState._storedEdges = null; },
        // Server-side layout
        setLayoutAlgorithm: function(algo) { apiState.layoutAlgorithm = algo || null; apiState.lastDataHash = null; },
        setAutoLayout: function(enabled) { apiState.autoLayout = enabled; apiState.lastDataHash = null; },
        getLayoutAlgorithm: function() { return apiState.layoutAlgorithm; },
        setLayoutConfig: function(cfg) { apiState.layoutConfig = cfg || null; apiState.lastDataHash = null; },
        getLayoutConfig: function() { return apiState.layoutConfig; },
        // Pointer-based graph data (checkpoint / bookmark preview)
        fetchGraphDataForPointer: fetchGraphDataForPointer,
        prefetchAllPointers: prefetchAllPointers,
        previewPointerGraph: previewPointerGraph,
        clearPointerGraphCache: clearPointerGraphCache,
        // MQL execution
        executeMQL: executeMQL,
        // Search Ask - text analysis + cursor loading
        searchAsk: searchAsk,
        // Cursor helpers
        createNewCursor: createNewCursor,
        saveMeCursor: saveMeCursor,
        resetMeCursor: resetMeCursor,
        refreshMeCursor: refreshMeCursor,
        clearGraph: clearGraph,
        showWarningNotification: showWarningNotification,
        fetchCursorHistory: fetchCursorHistory,
        fetchCursorState: fetchCursorState,
        refreshCursorPointers: refreshCursorPointers,
        fetchGraphDocuments: fetchGraphDocuments,
        navigateToBookmark: navigateToBookmark,
        applyPreWarmedTaskCursor: applyPreWarmedTaskCursor,
        hasStandaloneSnapshot: hasStandaloneSnapshot,
        // Selection window update
        updateSelectionWindow: updateSelectionWindow,
        // Getters
        get isLoading() { return apiState.isLoading; },
        get lastUpdate() { return apiState.lastUpdate; },
        get lastDataHash() { return apiState.lastDataHash; },
        get pollingEnabled() { return false; },  // Always false now
        get authMethod() { return apiState.authMethod; }
    };

})(typeof window !== 'undefined' ? window : this);
